﻿import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { ApplicationState } from '../../store';
import { AcceptBomsContext } from '../../store/Models/Api/AcceptBomsResponsesModels';
import { ResultType } from '../../store/Models/Api/ApiResponse';
import { DynamicRegulation } from '../../store/Models/Compliance/DynamicRegulation';
import { InviteSupplierModel } from '../../store/Models/Supplier/Supplier';
import * as Models from '../../store/Models/Surveys/SurveyDataModel';
import * as SelectSuppliersMaterialsStateController from '../../store/StateControllers/Supplier/SelectSuppliersMaterialsTableStateController';
import RevisionRequestManager from '../../store/StateControllers/Survey/RevisionRequestManager';
import * as SurveyStateController from '../../store/StateControllers/Survey/SurveyStateController';
import { ToxnotServiceClient } from '../../store/ToxnotServiceClient';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import CreateAndEditSurveySummaryPage from "../Survey/CreateAndEditSurveyPages/CreateAndEditSurveySummaryPage";
import Layout from './../Layout';
import { Page, PageContainer, SurveyActionBar, SurveySidebar } from '././SharedSurveyComponents';
import CreateAndEditSurveyMessagesPage from './CreateAndEditSurveyPages/CreateAndEditSurveyMessagesPage';
import CreateAndEditSurveyNotesPage from './CreateAndEditSurveyPages/CreateAndEditSurveyNotesPage';
import CreateAndEditSurveyQuestionsPage from './CreateAndEditSurveyPages/CreateAndEditSurveyQuestionsPage';
import CreateAndEditSurveyRemindersPage from './CreateAndEditSurveyPages/CreateAndEditSurveyRemindersPage';
import CreateAndEditSurveyStatusAndResponsesPage from './CreateAndEditSurveyPages/CreateAndEditSurveyStatusAndResponsesPage';
import { CreateAndEditSurveyComponentPaths } from './SurveyComponentPaths';
import { SurveyAcceptProgressModal } from './SurveyAcceptProgressModal';
import { OfflineProcessProgress, SurveyOfflineProgressModal } from './SurveyOfflineProgressModal';
import UserAddNamePromptModal from './UserAddNamePromptModal';
import { TeamRole } from '../../store/Models/UserContext';
import SelectFileForUpload from '../core/SelectFileForUpload';

import "./styles.css";
import { RegulationManager } from '../../store/Managers/RegulationManager';

// ===============================================================================

export type SurveyProps = Models.SurveyState &
    Models.SurveyResponse &
    SelectSuppliersMaterialsStateController.SelectSuppliersMaterialsTableState &
    typeof mapDispatchToProps &
    ToxnotComponentProperties &
    RouteComponentProps<
        { surveyId?: string, mode?: string, templateId?: string, startFromSurveyTemplate?: string, pageIndexToStartOn?: string },
        any,
        { supplierRowModels: InviteSupplierModel[] }>;
    

// ===============================================================================

enum OfflineResponseType {
    None,
    Manual,
    File
}

// ===============================================================================

enum OfflineResponseFileUploadStateType {
    Default,
    UploadingFile,
    UploadedFile
}

// ===============================================================================

interface LocalState {
    offlineResponseType: OfflineResponseType;
    offlineResponseFileUploadState: OfflineResponseFileUploadStateType;
    offlineProccessId: string | null;
    surveyResponse: Models.SurveyResponse | null;
}

// ===============================================================================

class CreateAndEditSurveysMain extends ToxnotSignInRequiredComponent<SurveyProps, LocalState> {
    // ===============================================================================

    private regulationChangedSideEffect?: (regulationId: string, regulationChecked: boolean) => void;

    // ===============================================================================

    constructor(props: SurveyProps) {
        super(props);
        this.assignStatusToUnAvailbleMaterials = this.assignStatusToUnAvailbleMaterials.bind(this);

        this.state = {
            offlineResponseType: this.getInitalOfflineResponseType(),
            offlineResponseFileUploadState: OfflineResponseFileUploadStateType.Default,
            offlineProccessId: null,
            surveyResponse: null
        };
    }

    // ===============================================================================

    private getInitalOfflineResponseType() {
        return this.getUserContextForSignedInUser().averyDennisonFeaturesEnabled || this.getUserContextForSignedInUser().userCapabilities.exportToOfflineSurveyEnabled ? OfflineResponseType.None : OfflineResponseType.Manual;
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    // ===============================================================================

    public async componentDidMount() {
        
        //Existing 
        if (this.props.match.params.surveyId && this.props.match.path === CreateAndEditSurveyComponentPaths.ExistingSurveyPath) {
            try {
                await this.props.getExistingSurveyState(this.props.match.params.surveyId);
            } catch {
                this.props.setSurveyUIData({ showNotFoundErrorScreen: true });
            }

            this.props.setSurveyUIData({ dataLoaded: true });          
        }
        //New
        else if (this.props.match.path === CreateAndEditSurveyComponentPaths.NewSurveyPath) {
            await this.props.getNewSurveyMetadata(this.getExistingInventoryIdsFromLocationState());
            if (this.props.match.params.startFromSurveyTemplate) {
                this.applyTemplate(this.props.match.params.startFromSurveyTemplate);
            }
            this.props.setSurveyUIData({ dataLoaded: true });
        }
        //Template
        else if (this.props.match.params.templateId !== undefined && this.props.match.path === CreateAndEditSurveyComponentPaths.EditSurveyTemplatePath) {
            await this.props.getNewSurveyMetadata(this.getExistingInventoryIdsFromLocationState(), true);
            this.applyTemplate(this.props.match.params.templateId);
            this.props.setSurveyUIData({ dataLoaded: true });
        }
        // Set current active page if provided in url.
        if (this.props.match.params.pageIndexToStartOn) {
            this.props.setSurveyUIData({ currentPage: parseInt(this.props.match.params.pageIndexToStartOn) });
        }

        const serviceClient = new ToxnotServiceClient();
        if (this.props.surveyRequest.requestId) {
            try {
                const apiResult = await serviceClient.getDataCollectionResponse(this.props.surveyRequest.requestId);
                this.setState({ surveyResponse: apiResult.data });
            }
            catch (error) {
                console.error("Failed to fetch survey response", error);
            }
        }
    }

    // ===============================================================================

    private getExistingInventoryIdsFromLocationState(): string[] {
        const sourceRowModels = this.props.location.state?.supplierRowModels ?? [];

        return sourceRowModels
            .selectMany(rowModel => rowModel.requestedMaterials)
            .map(invInfo => invInfo.inventoryId)
            .filter(inventoryId => inventoryId !== null)
            .map(inventoryId => inventoryId!);
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        let sideOptions: [string, number][];

        if ((this.props.surveyUi.currentState === Models.SurveyMode.EditExisting) && (this.props.surveyRequest.state !== 4)) {
            sideOptions = [
                [this.localizeString("Status & Responses"), 4],
                [this.localizeString("Notes"), 5],
                [this.getLocalizedOverviewSectionTitle(), 0],
                [this.localizeString("Requested Data"), 1],
            ];

            if (!this.obtainmentServicesEnabled()) {
                sideOptions.splice(1, 0, [this.localizeString("Reminders"), 2]);
                sideOptions.push([this.localizeString("Message Settings"), 3])
            }
        }
        else if (this.props.surveyUi.currentState === Models.SurveyMode.New || ((this.props.surveyUi.currentState === Models.SurveyMode.EditExisting) && (this.props.surveyRequest.state === 4))) {
            sideOptions = [
                [this.getLocalizedOverviewSectionTitle(), 0],
                [this.localizeString("Requested Data"), 1],
            ];

            if (!this.obtainmentServicesEnabled()) {
                sideOptions.push([this.localizeString("Reminders"), 2]);
                sideOptions.push([this.localizeString("Message Settings"), 3])
            }
        }
        else if (this.props.surveyUi.currentState === Models.SurveyMode.Template) {
            sideOptions = [
                [this.localizeString("Material Identifiers"), 0],
                [this.localizeString("Requested Data"), 1]
            ];
            if (!this.obtainmentServicesEnabled()) {
                sideOptions.push([this.localizeString("Reminders"), 2]);
                sideOptions.push([this.localizeString("Message Settings"), 3])
            }
        }
        else {
            sideOptions = [
                [this.localizeString("Requested Data"), 1],
            ];

            if (!this.obtainmentServicesEnabled()) {
                sideOptions.push([this.localizeString("Reminders"), 2]);
                sideOptions.push([this.localizeString("Message Settings"), 3])
            }
        }

        if (this.props.surveyUi.showErrorScreen) {
            return this.renderErrorMessage(this.localizeString("Sorry, there was a problem accessing the service. Please try again."), false);
        }

        if (this.props.surveyUi.showNotFoundErrorScreen) {
            return this.renderErrorMessage(this.localizeString("Selected survey cannot be found. Please try again."), true);
        }

        if (this.props.surveyUi.showMissingSupplierMessage) {
            return this.renderErrorMessage(this.localizeString("The supplier for this survey has been deleted. "), true);
        }

        if (!this.props.surveyUi.dataLoaded) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: true }}>
                    <div className="tn-section">
                        <LoadingSpinner size={LoadingSpinnerSize.Large} />
                    </div>
                </Layout>
            );
        }

        if (this.props.surveyUi.sendGranularRevisionRequestMode) {
            return (
                <Layout modalsRender={this.renderModals}>
                    {this.renderSendGranularRevisionRequestView()}
                </Layout>
            );
        }

        if (this.props.surveyUi.viewGranularRevisionRequestHistoryEntryId !== null) {
            return (
                <Layout modalsRender={this.renderModals}>
                    {this.renderViewGraularRevisionRequestView(this.props.surveyUi.viewGranularRevisionRequestHistoryEntryId)}
                </Layout>
            );
        }

        const createAndEditSurveyQuestionsPageProps = {
            // this will make the CreateAndEditSurveyQuestionsPage.regulationChangedSideEffect method accessible in this component via the callback function
            setRegulationChangedSideEffect: (cb: (regulationId: string, regulationChecked: boolean) => void) => this.regulationChangedSideEffect = cb
        }

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }} modalsRender={this.renderModals}>
                <div className="tn-section tn-survey-root">
                    {this.props.surveyUi.currentState === Models.SurveyMode.Template &&
                        <div className="tn-section-heading-row no-action-bar">
                            <h1><button type="button" className="btn btn-back" onClick={e => this.goBackToTemplateManager()}><span className="glyphicon glyphicon-chevron-left"></span></button> {this.localizeString("Edit Survey Template:") + " " + this.currentSelectedTemplateName()}</h1>
                        </div>}
                    {this.isUserEditor() && this.props.surveyUi.currentState !== Models.SurveyMode.Template && 
                        <SurveyActionBar>
                            {this.renderActionBar()}
                        </SurveyActionBar>
                    }
                    {this.props.surveyUi.currentState !== Models.SurveyMode.Template && 
                        <div id="general-survey-status-container">
                            <div className="status-container">
                                {this.renderGeneralSurveyInfo()}
                            </div>
                        </div>
                    }
                    <div id="offline-survey-status-container">
                        {this.renderOfflineProcessAlertManager()}
                    </div>
                    <div className="tn-survey-sidebar-and-body-container">
                        <SurveySidebar>
                            {sideOptions.map((value, index) => <li data-key={value.toString()} key={index.toString()} className={"sidebar-menu-item " + (this.props.surveyUi.currentPage === value[1] ? 'active' : "")}><a role="button" onClick={e => this.props.setSurveyUIData({ currentPage: value[1] })}>{value[0]}</a></li>)}
                        </SurveySidebar>
                        <div className="tn-survey-body">
                            <PageContainer>
                                <Page pageNumber={0} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveySummaryPage /> </Page>
                                <Page pageNumber={1} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveyQuestionsPage {...createAndEditSurveyQuestionsPageProps} /> </Page>
                                <Page pageNumber={2} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveyRemindersPage /> </Page>
                                <Page pageNumber={3} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveyMessagesPage /> </Page>
                                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4) && <Page pageNumber={4} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveyStatusAndResponsesPage  {...this.props} {...this.state.surveyResponse} /> </Page>}
                                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4) && <Page pageNumber={5} currentPage={this.props.surveyUi.currentPage}> <CreateAndEditSurveyNotesPage /> </Page>}
                            </PageContainer>
                        </div>
                    </div>
                </div>
                <UserAddNamePromptModal onConfirm={this.userAddNamePromptModal_confirm}/>
            </Layout>
        );
    }

    // ===============================================================================

    private userAddNamePromptModal_confirm = (name: string) => {
        this.props.setSurveyRequestData({ fromName: name });

        if (this.props.surveyRequest.fromName === "") {
            return;
        }
        else if (this.props.surveyRequest.languageList.length > 1) {
            $("#select-language-to-send-in-modal").modal("show");
        }
        else {
            this.sendSurveyWithPotentialSaveChangesOption();
        }
    }

    // ===============================================================================

    private currentSelectedTemplateName() {
        return this.props.surveyMetadata.templates.firstOrDefault(template => template.templateId === this.props.surveyUi.currentTemplateId)?.name ?? "";
    }

    // ===============================================================================

    private goBackToTemplateManager() {
        this.props.setSurveyUIData({ currentTemplateId: null });
        this.props.history.goBack();
    }

    private isUserEditor = () => this.props.appServices.userContext?.isEditor ?? false;

    private obtainmentServicesEnabled = () => this.props.appServices.userContext?.userCapabilities.obtainmentServicesEnabled ?? false;

    private anyExtensionRequested = () => (this.props.surveyMetadata?.extensionRequest?.inventoryIds ?? []).length > 0 || this.props.surveyRequest.state == 6 || this.props.surveyRequest.state == 8;

    private canSendReminderSurveyState = () => ([0,1,6,7].includes(this.props.surveyRequest.state));

    // ===============================================================================

    private renderActionBar() {

        const linkedMaterialsExists = SurveyStateController.doLinkedMaterialsExist(this.props);

        return (
            <React.Fragment>
                {(this.props.surveyUi.currentState === Models.SurveyMode.New) &&
                    <button type="button" id="save-as-draft-button" className="btn btn-default" disabled={this.props.surveyRequest.dataRequested.materialDisclosureAsked && linkedMaterialsExists} onClick={e => this.saveSurveyAsDraftButton_click()}>{this.localizeString("Save As Draft")}</button>
                }
                {(this.props.surveyUi.currentState === Models.SurveyMode.New || this.props.surveyRequest.state === 4) && this.isUserEditor() &&
                    <button type="button" id="send-survey-button" className="btn btn-toxnot-primary" disabled={this.props.surveyRequest.dataRequested.materialDisclosureAsked && linkedMaterialsExists} onClick={this.sendSurveyButton_click}>{this.localizeString("Send Survey")}</button>
                }
                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4 && this.isUserEditor()) &&
                    <button disabled={this.anyExtensionRequested()} type="button" title={this.props.surveyRequest.state == 6 ? this.localizeString("Cannot send survey again until revision request(s) are submitted.") : ""} id="send-survey-again-button" onClick={e => this.sendSurveyAgainButton_click()} className="btn btn-toxnot-primary">{this.localizeString("Send Survey Again")}</button>
                }
                {this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4 && this.canSendReminderSurveyState() && this.isUserEditor() && !this.obtainmentServicesEnabled() &&
                    <button type="button" id="send-reminder-button" onClick={e => this.sendReminderButton_click()} className="btn btn-default">{this.localizeString("Send Reminder")}</button>
                }
                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && [0, 1, 6, 7, 9, 10].findIndex(v => v === this.props.surveyRequest.state) !== -1) && this.isUserEditor() &&
                    <button type="button" id="add-offline-response-button" onClick={e => this.showOfflineResponseButton_click()} className="btn btn-default">{this.localizeString("Add Offline Response")}</button>
                }
                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4 && this.props.surveyRequest.requireValidationOnResponse) && this.isUserEditor() && 
                    <button type="button" onClick={e => this.removeValidationButton_click()} className="btn btn-danger">{this.localizeString("Remove Validation from Survey")}</button>
                }
                {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting && this.props.surveyRequest.state !== 4 && this.getUserContextForSignedInUser().userCapabilities.exportToOfflineSurveyEnabled) &&
                    <button type="button" id="export-to-offline-request-button" onClick={e => this.exportSurveyButton_click()} className="btn btn-default">{this.localizeString("Export to Offline Request")}</button>
                }
                {this.isUserEditor() && this.props.surveyUi.currentState !== Models.SurveyMode.Template && 
                    <div className="btn-group">
                        <button type="button" className="btn btn-default dropdown-toggle template-dropdown-button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{this.localizeString("Templates")} <span className="caret"></span></button>
                        <ul id="languages-dropdown-menu" className="dropdown-menu">
                            <li>
                                <a id="save-survey-template-button" onClick={e => this.saveTemplateAsButton_click()} role="button">{this.localizeString("Save Current Settings as a Template...")}</a>
                            </li>
                            {((this.props.surveyUi.currentState === Models.SurveyMode.New) || (this.props.surveyRequest.state === 4)) &&
                                <li>
                                    <a id="apply-survey-template-button" data-toggle="modal" href="#survey-template-apply-modal" role="button">{this.localizeString("Apply a Template...")}</a>
                                </li>
                            }
                        </ul>
                    </div>
                }
                {/*<div className="btn-group">*/}
                {/*    <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{this.localizeString("Languages")} <span className="caret"></span></button>*/}
                {/*    <ul id="languages-dropdown-menu" className="dropdown-menu">*/}
                {/*        {this.isUserEditor() && <li><a role="button" onClick={e => this.addLanguageButton_click()}>{this.localizeString("Add Language...")}</a></li>}*/}
                {/*        {(this.props.surveyRequest.languageList.length > 0) && this.isUserEditor() &&  <li><a role="button" onClick={e => this.removeLanguageButton_click()}>{this.localizeString("Remove Language...")}</a></li>}*/}
                {/*        <li role="separator" className="divider"></li>*/}
                {/*        {this.props.surveyRequest.languageList.map((value, index) => (*/}
                {/*            <li key={index.toString()}><a onClick={e => this.props.setSurveyUIData({ currentLanguage: value })} role="button">{this.props.appServices.getLocalizedDisplayNameForCultureName(value)}{(value === this.props.surveyUi.currentLanguage) && (<React.Fragment>{" "}<span className="glyphicon glyphicon-ok"></span></React.Fragment>)}</a></li>*/}
                {/*        ))}*/}
                {/*    </ul>*/}
                {/*</div>*/}
                {/*{this.localizeString("Current Language:")} <span className="language-tag">{this.props.appServices.getLocalizedDisplayNameForCultureName(this.props.surveyUi.currentLanguage)}</span>*/}
            </React.Fragment>
        );
    }

    // ===============================================================================

    private renderErrorMessage = (message: string, renderBackButtonToSuppliers: boolean) => {
        return (
            <Layout options={{ pinAppContentToWindowHeight: true }}>
                <div className="tn-body">
                    <div className="tn-section">
                        <div className="tn-section-row">
                            <div className="tn-section-col-offset-3 tn-section-col-6">
                                <div id="substance-detail-error-panel" className="panel panel-default">
                                    <div className="panel-body">
                                        {message}
                                        <br /> 
                                        {renderBackButtonToSuppliers && <a href="/Suppliers/Suppliers">{this.localizeString("Go Back")}</a>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    // ===============================================================================

    private getLocalizedOverviewSectionTitle() {
        return this.props.surveyRequest.bomType === 2 ? this.localizeString("Suppliers") : this.localizeString("Materials");
    }

    // ===============================================================================

    private renderGeneralSurveyInfo() {
        const surveyName = (this.props.surveyRequest.surveyName ?? "") !== "" ? (<span><label>{this.localizeString("Survey Name:")}</label> {this.props.surveyRequest.surveyName} &bull; </span>) : <span></span>;
        return (
            <p>
                {surveyName}<label>{this.localizeString("Suppliers(s):")}</label> {this.getSupplierNames(this.props.surveyRequest.supplierId)} {this.getMoreThanFiveSuppliersLink(this.props.surveyRequest.supplierId)} {this.getSentToInfo()}
            </p>
        );
    }

    // ===============================================================================

    private renderOfflineProcessAlertManager() {
        return (
            this.props.surveyMetadata?.offlineProcessDataResponses?.map((operation) => {
                let localizedActionDescription = Models.OfflineProcessAlertManagerValues.getProcessingTypeToFriendlyDescriptionMap(this.props.appServices)[operation.operationType];

                if (operation.state.processingStage === Models.OfflineProcessingStageType.Failed.toString()) {
                    let localizedFailedDescription = Models.OfflineProcessAlertManagerValues.getProcessingTypeToFailedDescriptionMap(this.props.appServices)[operation.operationType];

                    return (
                        <div key={operation.operationId} className="alert alert-warning p-relative">
                            {localizedFailedDescription}&nbsp;
                            <button onClick={async e => await this.removeOfflineProcessAlert_click(e)} role="button" className="delete-offline-failed-operation-button" data-offline-process-id={operation.operationId}>
                                <span className="glyphicon glyphicon-remove"></span>
                            </button>
                        </div>
                    );
                } else {
                    if (operation.operationType == Models.OfflineProcessType.SendDraftSurvey.toString()) {
                        if (!operation.context.shouldSend) {
                            localizedActionDescription = Models.OfflineProcessAlertManagerValues.getProcessingTypeToFriendlyDescriptionMap(this.props.appServices)[Models.OfflineProcessType.SendDraftSurvey];
                        }
                    }

                    if (operation.state.processingStage !== Models.OfflineProcessingStageType.TotallyComplete.toString()) {
                        return (
                            <div key={operation.operationId} className="alert alert-info p-relative">
                                {localizedActionDescription}&nbsp;
                                <a onClick={async e => await this.openOfflineSurveyStatusModal_click(e)} className="alert-link in-progress-status-link" role="button" data-offline-process-type={operation.operationType} data-offline-process-id={operation.operationId}>
                                    {this.props.appServices.localizeString('Click here')}
                                </a>
                                {this.props.appServices.localizeString(' to view the status')}.
                            </div>
                        );
                    } else if (this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled && operation.results?.affectedCustomerIds?.length > 0) {
                        let localizedSuccessDescription = Models.OfflineProcessAlertManagerValues.getProcessingTypeToSuccessDescriptionMap(this.props.appServices)[operation.operationType];

                        return (
                            <div key={operation.operationId} className="alert alert-success p-relative">
                                {localizedSuccessDescription}&nbsp;
                                <a onClick={async e => await this.openUpdateClientsModal_click(e)} data-updated-inventory-ids={operation.results?.updatedLibraryInventoryIds} className="alert-link in-progress-status-link" role="button" data-offline-process-type={operation.operationType} data-offline-process-id={operation.operationId}>
                                    {this.props.appServices.localizeString('Click here')}
                                </a>
                                {this.props.appServices.localizeString(' to update your customers')}.
                                <button onClick={async e => await this.removeOfflineProcessAlert_click(e)} role="button" className="delete-offline-failed-operation-button" data-offline-process-id={operation.operationId}>
                                    <span className="glyphicon glyphicon-remove"></span>
                                </button>
                            </div>
                        );
                    }
                }
            })
        );
    }

    // ===============================================================================

    private async removeOfflineProcessAlert_click(e: any) {
        e.persist();

        const serviceClient = new ToxnotServiceClient();
        const offlineProcessId = $(e.currentTarget).data("offline-process-id");
        const deleteResult = await serviceClient.offlineProcessDelete(offlineProcessId);

        if (!deleteResult.success) {
            alert(this.props.appServices.localizeString("Sorry, there was a problem accessing the service. Please try again."));
            return;
        }

        const offlineProcessDataResponses = this.props.surveyMetadata.offlineProcessDataResponses;
        const filteredOfflineProcessDataResponses = offlineProcessDataResponses.filter(i => i.operationId !== offlineProcessId);

        this.props.setSurveyMetadata({ offlineProcessDataResponses: filteredOfflineProcessDataResponses });
        
        $(e.currentTarget).parent("div.alert").hide();
    }

    // ===============================================================================

    private async openUpdateClientsModal_click(e: any) {
        e.persist();

        const updatedInventoryIds = $(e.currentTarget).data("updated-inventory-ids").split(",");
        const operationId = $(e.currentTarget).data("offline-process-id");

        await this.promptUserToUpdateCustomers(updatedInventoryIds, operationId);
    }

    // ===============================================================================

    private async openOfflineSurveyStatusModal_click(e: any) {
        e.persist();

        if (this.props.surveyUi.activeAcceptOfflineProcessId !== "") {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        const offlineProcessId = $(e.currentTarget).data("offline-process-id");
        const $modal = $("#offline-accept-progress-modal");

        $modal.off("hidden.bs.modal").on("hidden.bs.modal", async () => {
            if (this.props.match.params.surveyId && this.props.match.path === CreateAndEditSurveyComponentPaths.ExistingSurveyPath) {
                try {
                    await this.props.getExistingSurveyState(this.props.match.params.surveyId);
                } catch {
                    this.props.setSurveyUIData({ showNotFoundErrorScreen: true });
                }

                this.props.setSurveyUIData({ dataLoaded: true });
            }

            this.props.setSurveyUIData({ activeAcceptOfflineProcessId: "" });
        });

        this.props.setSurveyUIData({ activeAcceptOfflineProcessId: offlineProcessId });

        $modal.modal("show");
    }

    // ===============================================================================

    private getMoreThanFiveSuppliersLink(supplierId: string | null) {
        if ((supplierId === null) && (this.props.supplierRowModels.length > 5)) {
            return <a role="button" onClick={e => this.props.goToMaterialsTab()}> {this.props.supplierRowModels.length - 5} {this.localizeString("additional supplier(s)")} </a>;
        }

        return null;
    }

    // ===============================================================================

    private getSupplierNames(supplierId: string | null) {
        if (supplierId === null) {
            if (this.props.supplierRowModels.length > 5) {
                return this.props.supplierRowModels.slice(0, 5).map(s => s.supplierName).join(", ").coalesceToMaxSizeAndIndicateTruncationWithEllipsis(100) + ", " ;
            }
            else {
                return this.props.supplierRowModels.map(s => s.supplierName).join(", ").coalesceToMaxSizeAndIndicateTruncationWithEllipsis(100);
            }
        }

        return this.props.surveyRequest.sendToCompany ?? this.props.appServices.localizeString("Unknown Supplier");
    }

    // ===============================================================================

    private getSentToInfo() {
        if ((this.props.surveyRequest.sendToEmail ?? "") === "") {
            return null;
        }
       
        return (
            <React.Fragment>
                &bull; <label>{this.props.surveyRequest.state === Models.SurveyStatusState.Draft ? this.localizeString("Sending To:") : this.localizeString("Sent To:")}</label> ({this.props.surveyRequest.sendToEmail})
            </React.Fragment>
        );
    }

    // ===============================================================================

    // Modals

    // ===============================================================================


    private renderModals = () => {

        // Check for CMRTv2 Compliance
        const hasCMRTv2Data = this.props?.surveyRequest.dataRequested.complianceList?.includes(RegulationManager.ConflictMineralsV2RegulationId);
        const isCompanyScope = this.state.surveyResponse?.supplierQuestionnaireData?.questionnaire?.
            pages.find(x => x.regulationId == RegulationManager.ConflictMineralsV2SupplierQuestionnaireRegulationId)?.
            questions.find(x => x.summaryFieldKey == "cmrt_declaration_of_scope_or_class")?.value.toLowerCase() == "company";

        return (
            <React.Fragment>
                <div id="ask-for-general-revision-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Ask For a Revision")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label id="revision-message-title">{this.localizeString("Revision Instructions")}</label>
                                    <textarea id="revision-message-textarea" className="form-control" rows={12}></textarea>
                                </div>
                                <div className="alert alert-info initially-hidden">{this.localizeString("Sending...")}</div>
                                <div className="alert alert-success initially-hidden">{this.localizeString("Sent Successfully")}</div>
                                <div className="alert alert-warning initially-hidden">{this.localizeString("Your email connection has expired. Please reconnect your account.")}</div>
                                <div className="alert alert-danger initially-hidden">{this.localizeString("Sorry, there was a problem accessing the service. Please try again.")}</div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" id="submit-revision-cancel-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" id="submit-revision-submit-button" className="btn btn-toxnot-primary" onClick={async e => await this.generalRevisionModalSubmitButton_click()}>{this.localizeString("Send")}</button>
                                <button type="button" id="submit-revision-done-button" className="btn btn-toxnot-primary initially-hidden" data-dismiss="modal">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="empty-granular-revision-requests-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Send a Revision Request")}</h4>
                            </div>
                            <div className="modal-body">
                                <p>
                                    {this.localizeString("You don't have any specific revisions requested.")}
                                </p>
                                <p>
                                    {this.localizeString("Would you like to send a general revision request for the entire survey, or ask for revisions on specific products or questions?")}
                                </p>
                                <div className="alert alert-info">
                                    {this.localizeString("To ask for revisions on specific products or questions, use the \"Edit Revision Request\" buttons on the Survey page.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button disabled={this.anyExtensionRequested()} type="button" className="btn btn-default" onClick={e => this.sendGeneralRevisionRequestButton_click()}>{this.localizeString("Send a General Revision Request")}</button>
                                <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Edit Specific Revision Requests")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="sending-revision-requests-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Sending Revision Request...")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    {this.localizeString("Sending request email...")}
                                </div>
                                <div className="alert alert-success">
                                    {this.localizeString("Your request was sent!")}
                                </div>
                                <div className="alert alert-warning initially-hidden">
                                    {this.localizeString("Your email connection has expired. Please reconnect your account.")}
                                </div>
                                <div className="alert alert-danger">
                                    {this.localizeString("Sorry, there was a problem accessing the service.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-toxnot-primary" onClick={e => this.sendRevisionsRequestsDoneButton_click()}>{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <p>{this.localizeString("Some of the selected suppliers are missing contact information. Please edit their contact information (email address) first and then create a survey.")}</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={e => history.back()} className="btn btn-toxnot-primary">{this.localizeString("Remove Validation")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="send-survey-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Send Survey")}</h4>
                            </div>
                            <div className="modal-body">
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendingSurvey &&
                                    <div className="alert alert-info">{this.localizeString("Sending...")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveyFailed &&
                                    <div className="alert alert-danger">{this.localizeString("Sorry, there was a problem sending the survey. Please try again.")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveyRefreshTokenFailed && 
                                    <div className="alert alert-warning">{this.localizeString("Your email connection has expired. Please reconnect your account.")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveyContainsLinkedMaterials &&
                                    <div className="alert alert-warning">{this.localizeString("Sorry, the selected product/material contains linked materials. Toxnot can't send surveys that include linked materials.")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveyContainsInvalidFromEmaillAddress &&
                                    <div className="alert alert-warning">{this.localizeString("Sorry, the requestor email contains an invalid email address. Please change it to a valid one to send a survey.")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveyContainsInvalidCcEmailList &&
                                    <div className="alert alert-warning">{this.localizeString("Sorry, one of the emails in the CC Email list contains an invalid email address. Please change it to a valid one to send a survey.")}</div>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveySucceeded &&
                                    <div className="alert alert-success">{this.localizeString("Survey sent!")}</div>}
                            </div>
                            <div className="modal-footer">
                                {this.props.surveyUi.surveySendStateType !== Models.SurveySendStateType.SendSurveySucceeded &&
                                    <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>}
                                {this.props.surveyUi.surveySendStateType === Models.SurveySendStateType.SendSurveySucceeded &&
                                    <button type="button" className="btn btn-toxnot-primary" onClick={e => window.location.href = "/Surveys/SupplierSurveys"}>{this.localizeString("Done")}</button>}
                            </div>
                        </div>
                    </div>
                </div>
                <div id="remove-validation-warning-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Remove Validation")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <p>{this.localizeString("Are you sure you want to let your supplier submit their response with required columns or required questions missing?")}</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" onClick={e => this.removeValidationConfirmationButton_click() } className="btn btn-toxnot-primary">{this.localizeString("Remove Validation")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="survey-update-confirmation-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Confirm Survey Update")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <p>{this.localizeString("When you send a survey update, the supplier's progress in the previous questionnaire and materials will be lost.")}</p>
                                    <p>{this.localizeString("Do you wish to continue?")}</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" onClick={e => this.sendSurveyAgainUpdateConfirmationBtn_click()} className="btn btn-toxnot-primary">{this.localizeString("Send Survey Update")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="send-reminder-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Send Reminder")}</h4>
                            </div>
                            <div className="modal-body">
                                {this.props.surveyUi.sentReminderAlert &&
                                    <div className="alert alert-success" role="alert">
                                        <p>{this.localizeString("Reminder Sent Successfully!")}</p>
                                    </div>
                                }
                                {this.props.surveyUi.errorReminderAlert &&
                                    <div className="alert alert-warning" role="alert">
                                        <p>{this.localizeString("An error occurred! Please try again later.")}</p>
                                    </div>
                                }
                                {this.props.surveyUi.errorReminderNoMessageAlert &&
                                    <div className="alert alert-warning" role="alert">
                                        <p>{this.localizeString("Please enter a message.")}</p>
                                    </div>
                                }
                                {!this.props.surveyUi.sentReminderAlert &&
                                    <React.Fragment>
                                        <p>{this.localizeString("Send a reminder email notification to your supplier.")}</p>
                                        <div className="form-group">
                                            <label>{this.localizeString("Message")}</label>
                                            <textarea id="reminder-message" className="form-control" value={this.props.surveyUi.sendReminderMessage} onChange={e => this.props.setSurveyUIData({ sendReminderMessage: e.target.value })} rows={5} placeholder="" />
                                        </div>
                                    </React.Fragment>
                                }
                            </div>
                            <div className="modal-footer">
                                {this.props.surveyUi.sentReminderAlert
                                    ? <button type="button" id="send-reminder-done-button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Done")}</button>
                                    : (
                                        <React.Fragment>
                                            <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                            <button type="button" id="send-reminder-submit-button" onClick={e => this.props.sendReminder()} className="btn btn-toxnot-primary">{this.localizeString("Send Reminder")}</button>
                                        </React.Fragment>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div id="survey-template-apply-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Apply a Template")}</h4>
                            </div>
                            <div className="modal-body">
                                {this.props.surveyMetadata.templates.length === 0 &&
                                    <p>{this.localizeString("No templates found.")}</p>
                                }
                                <table className="table tn-table">
                                    <thead>
                                        <tr>
                                            <td>{this.localizeString("Name")}</td>
                                            <td></td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.props.surveyMetadata.templates.map((data, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>{data.name}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-default apply-template-button" onClick={e => this.applyTemplate(data.templateId ?? "")}>{this.localizeString("Select")}</button>
                                                    </td>
                                                </tr>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="survey-template-save-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Save Template")}</h4>
                            </div>
                            <div className="modal-body">
                                {(this.props.surveyUi.saveTemplateMode !== Models.SaveTemplateMode.Default) &&
                                    <div className="alert alert-warning">
                                        {this.localizeString("You made changes to the template - Do you want to save them?")}
                                    </div>
                                }
                                <div>
                                    <div>
                                        <div className="radio">
                                            <label>
                                                <input type="radio" name="saveTemplateRadio" checked={this.props.surveyUi.saveTemplateAnyRadioSelected && (this.props.surveyUi.saveTemplateSelectedTemplateId === null)} onChange={e => { if (e.target.checked) this.props.setSurveyUIData({ saveTemplateAnyRadioSelected: true, saveTemplateSelectedTemplateId: null }) }} /> {this.localizeString("Make a new Template")}
                                            </label>
                                        </div>
                                        {(this.props.surveyUi.saveTemplateSelectedTemplateId === null) &&
                                            <div style={{ paddingLeft: "50px" }}>
                                                <label>{this.localizeString("Template Name")}</label>
                                                <input id="save-survey-template-name" type="text" className="form-control" value={this.props.surveyUi.newTemplateName} onChange={e => this.props.setSurveyUIData({ newTemplateName: e.target.value })} />
                                            </div>
                                        }
                                    </div>
                                    <div>
                                        {this.props.surveyMetadata.templates.orderBy(templateModel => templateModel.name).map((templateModel, index) => (
                                            <div key={index} className="radio">
                                                <label>
                                                    <input type="radio" name="saveTemplateRadio" checked={this.props.surveyUi.saveTemplateAnyRadioSelected && (this.props.surveyUi.saveTemplateSelectedTemplateId === templateModel.templateId)} onChange={e => { if (e.target.checked) this.props.setSurveyUIData({ saveTemplateAnyRadioSelected: true, saveTemplateSelectedTemplateId: templateModel.templateId }) }} /> {templateModel.name.emptyCoalesce(this.localizeString("Unnamed Template"))}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                {(this.props.surveyUi.saveTemplateMode === Models.SaveTemplateMode.Default) &&
                                    <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                }
                                <button id="save-survey-template-submit-button" type="button" onClick={e => this.saveTemplateButton_click()} className="btn btn-toxnot-primary" data-dismiss="modal">{(this.props.surveyUi.saveTemplateMode === Models.SaveTemplateMode.Default) ? this.localizeString("Save") : this.localizeString("Continue")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="remove-language-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Remove Language")}</h4>
                            </div>
                            <div className="modal-body">
                                <div id="remove-language-selection-container">
                                    <label>{this.localizeString("Please select a language to remove:")}</label>
                                    <div id="languages-for-removal-container">
                                        {this.props.surveyRequest.languageList.map((language, index) => {
                                            const allowRemoval = (language !== this.props.surveyRequest.nativeLanguage);
                                            return (
                                                <p key={index}>
                                                    {this.props.appServices.getLocalizedDisplayNameForCultureName(language)}
                                                    {" "}
                                                    {allowRemoval &&
                                                        <button type="button" className="btn btn-toxnot-danger" onClick={e => this.removeLanguageSubmitButton_click(language)}><span className="glyphicon glyphicon-remove"></span></button>}
                                                </p>
                                            );
                                        })
                                        }
                                    </div>
                                </div>
                                <div id="remove-language-confirmation-container">
                                    <div className="alert alert-warning">
                                        {this.localizeString("Are you sure you want to remove the language")} <span id="language-for-removal-confirmation-span"></span>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="add-language-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Add a Language")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <p>
                                        {this.localizeString("You can write Survey content in multiple languages, allowing suppliers to choose the best language for them.")}
                                    </p>
                                </div>
                                <div className="form-group">
                                    <label>{this.localizeString("Select the language you'd like to add:")}</label>
                                    <select id="new-language-selector" className="form-control">
                                        <option value="en">{this.localizeString("English")}</option>
                                        <option value="zh">{this.localizeString("Chinese")}</option>
                                        <option value="fr">{this.localizeString("French")}</option>
                                        <option value="de">{this.localizeString("German")}</option>
                                        <option value="it">{this.localizeString("Italian")}</option>
                                        <option value="ja">{this.localizeString("Japanese")}</option>
                                        <option value="pt">{this.localizeString("Portuguese")}</option>
                                        <option value="es">{this.localizeString("Spanish")}</option>
                                        <option value="th">{this.localizeString("Thai")}</option>
                                    </select>
                                </div>
                                <div className="alert alert-warning">
                                    {this.localizeString("That language already exists in your Survey. Please select a different language.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" className="btn btn-toxnot-primary" onClick={e => this.addLanguageSubmitButton_click() }>{this.localizeString("Add Language")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="select-language-to-send-in-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{this.localizeString("Select the Language to Send the Survey Email In")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>{this.localizeString("Select the Language to send the Survey Email in:")}</label>
                                    <select className="form-control" id="language-to-send-in-selector" value={this.props.surveyRequest.languageToSendIn ?? (this.props.surveyRequest.nativeLanguage ?? "")} onChange={e => this.props.setSurveyRequestData({ languageToSendIn: e.target.value })}>
                                        {this.props.surveyRequest.languageList.map((language, index) => (
                                            <option key={index} value={language}>{this.props.appServices.getLocalizedDisplayNameForCultureName(language)}</option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" className="btn btn-toxnot-primary" onClick={this.selectLanguageToSendInSubmitButton_click}>{this.localizeString("Send Surveys")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="add-questionnaire-answer-that-makes-bom-data-required-modal" className="modal" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Select Question and Answer")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    {this.localizeString("Only multiple choice questions can be selected. There are no questions to choose from.")}
                                </div>
                                <div className="alert alert-info">
                                    <p>
                                        {this.localizeString("Select the page, question, and answer that you want to make BOM data required.")}
                                    </p>
                                    <p>
                                        {this.localizeString("Only multiple choice questions can be selected.")}
                                    </p>
                                </div>
                                <div id="page-question-answer-selection-container">
                                    <div className="form-group">
                                        <label>{this.localizeString("Select Page:")}</label>
                                        <select className="form-control page-select">
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>{this.localizeString("Select Question:")}</label>
                                        <select className="form-control question-select">
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>{this.localizeString("Select Answer:")}</label>
                                        <select className="form-control answer-select">
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" id="add-questionnaire-answer-that-makes-bom-data-required-submit-button" className="btn btn-toxnot-primary">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="cmrt-revision-request-confirmation-dialog" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Send Revision Request")}</h4>
                            </div>
                            <div className="modal-body">
                                {(hasCMRTv2Data) && (
                                    <div className="alert-warning p-10">
                                        {this.localizeString("Heads up: If the supplier updates company-level compliance information for Conflict Minerals, their changes will overwrite responses across all inventories in the original survey request. Do you want to continue?")}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" id="cmrt-revision-request-confirmation-dialog-cancel-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" id="cmrt-revision-request-confirmation-dialog-confirm-button" className="btn btn-toxnot-primary" onClick={this.sendSpecificRevisionsConfirmClick}>{this.localizeString("Confirm")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="accept-supplier-response-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Accept Supplier Questionnaire Response")}</h4>
                            </div>
                            <div className="modal-body">
                                <p>
                                    {this.localizeString("Accepting this Questionnaire Response will copy the Questionnaire response data to your Supplier Summary Data.")}
                                </p>
                                {(hasCMRTv2Data && isCompanyScope) && (
                                    <div className="alert-warning p-10">
                                        {this.localizeString("Material response data will be populated from the supplier’s Company-Scope CMRT survey response.")}
                                    </div>
                                )}
                                <div className="alert alert-info">
                                    {this.localizeString("Processing...")}
                                </div>
                                <div className="alert alert-success">
                                    {this.localizeString("Response accepted!")}
                                </div>
                                <div className="alert alert-danger">
                                    {this.localizeString("Sorry, there was a problem processing the response. Please try again.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" id="accept-supplier-response-cancel-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" id="accept-supplier-response-confirm-button" className="btn btn-toxnot-primary" onClick={async e => await this.acceptSupplierResponseConfirmButton_click()}>{this.localizeString("Accept Response")}</button>
                                <button type="button" id="accept-supplier-response-done-button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="accept-bom-responses-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Accept Survey Responses")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="accept-bom-responses-normal-container">
                                    <p>
                                        {this.localizeString("Accepting these Supplier Survey Responses will add the data to your library. You can either choose to either overwrite the data which will overwrite the data in the material you selected for the survey, or you can save the data as new Products or Materials. If you've already saved the response as a copy, then overwriting with overwrite the data in that saved copy.")}
                                    </p>
                                    {(hasCMRTv2Data && isCompanyScope) && (
                                        <div className="alert-warning p-10">
                                            {this.localizeString("Material response data will be populated from the supplier’s Company-Scope CMRT survey response.")}
                                        </div>
                                    )}
                                    <div className="radio">
                                        <label>
                                            <input type="radio" name="acceptResponseRadio" id="accept-bom-responses-radio-overwrite-original" />
                                            <b>{this.localizeString("Overwrite")}</b>
                                        </label>
                                    </div>
                                    <div className="radio">
                                        <label>
                                            <input type="radio" name="acceptResponseRadio" id="accept-bom-responses-radio-save-copy" />
                                            <b>{this.localizeString("Save a Copy")}</b>
                                        </label>
                                    </div>
                                </div>
                                <div className="accept-bom-responses-open-container">
                                    <p>
                                        {this.localizeString("Accepting these Supplier Survey Responses will add them to your library as copies.")}
                                    </p>
                                </div>
                                <div className="alert alert-info">
                                    {this.localizeString("Processing...")}
                                </div>
                                <div className="alert alert-success">
                                    <p>
                                        {this.localizeString("Responses accepted!")}
                                    </p>
                                </div>
                                <div className="alert alert-warning">
                                    <p>
                                        {this.localizeString("Sorry, your Products/Materials Library quota is full and you can't add any more items to your Library.")}
                                    </p>
                                    <p>
                                        <a href="/Account/Upgrade" target="_blank">{this.localizeString("Check out your Subscription Page Here to see options.")}</a>
                                    </p>
                                </div>
                                <div className="alert alert-danger">
                                    {this.localizeString("Sorry, there was a problem processing the responses. Please try again.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" id="accept-bom-responses-cancel-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                <button type="button" id="accept-bom-responses-confirm-button" className="btn btn-toxnot-primary" onClick={async e => await this.acceptBomResponsesConfirmButton_click()}>{this.localizeString("Accept Responses")}</button>
                                <button type="button" id="accept-bom-responses-done-button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                {this.renderAddOfflineResponseModal()}
                <div id="offline-survey-progress-modal" className="modal fade" tabIndex={-1} role="dialog">
                    <div className="modal-dialog">
                        {this.props.surveyUi.activeSendOfflineProcessId !== ""
                            ? <SurveyOfflineProgressModal appServices={this.props.appServices} offlineProcessId={this.props.surveyUi.activeSendOfflineProcessId} showDataSaveMessage={this.props.surveyUi.showDataSaveMessage} />
                            : <div className="modal-content">
                                <div className="modal-header">
                                    <button type="button" id="offline-progress-x-close-button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                    <h4 className="modal-title">{this.props.appServices.localizeString("Offline Survey Status")}</h4>
                                </div>
                                <div className="modal-body">
                                    <LoadingSpinner />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" id="offline-progress-close-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Done")}</button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div id="offline-accept-progress-modal" className="modal fade" tabIndex={-1} role="dialog">
                    <div className="modal-dialog">
                        {this.props.surveyUi.activeAcceptOfflineProcessId !== ""
                            ? <SurveyAcceptProgressModal appServices={this.props.appServices} offlineProcessId={this.props.surveyUi.activeAcceptOfflineProcessId} />
                            : <div className="modal-content">
                                <div className="modal-header">
                                    <button type="button" data-id="offline-progress-x-close-button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                    <h4 className="modal-title">{this.props.appServices.localizeString("3E Exchange is accepting your supplier response")}</h4>
                                </div>
                                <div className="modal-body">
                                    <LoadingSpinner />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" data-id="offline-progress-close-button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Done")}</button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div id="assign-status-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close" style={{marginTop: '-2px'}}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h4 className="modal-title">{this.props.appServices.localizeString("Assign Statuses")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="assign-statuses-container">
                                    <h5>{this.props.appServices.localizeString("Product/Material Status")}</h5>

                                    <div id='assign-status-modal-status-options' className="status-options">
                                        
                                    </div>

                                    <div className="checkbox" style={{ marginTop: "15px" }}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                id="mark-workflow-status-obsolete"
                                            />
                                            {this.props.appServices.localizeString("Mark Workflow Status as Obsolete")}
                                        </label>
                                    </div>
                                </div>

                                {/* Alert sections - initially hidden, shown based on state */}
                                <div className="alert alert-info processing-alert" style={{ display: 'none' }}>
                                    {this.props.appServices.localizeString("Processing...")}
                                </div>
                                <div className="alert alert-success success-alert" style={{ display: 'none' }}>
                                    <p>
                                        {this.props.appServices.localizeString("Status assigned successfully!")}
                                    </p>
                                </div>
                                <div className="alert alert-danger error-alert" style={{ display: 'none' }}>
                                    {this.props.appServices.localizeString("Sorry, there was a problem assigning the status. Please try again.")}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-default"
                                    data-dismiss="modal"
                                >
                                    {this.props.appServices.localizeString("Cancel")}
                                </button>
                                <button
                                    type="button"
                                    id="assign-status-confirm-button"
                                    className="btn btn-primary assign-btn"
                                    onClick={this.assignStatusToUnAvailbleMaterials}
                                >
                                    {this.props.appServices.localizeString("Assign")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="prompt-to-update-customers-modal" className="modal fade" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Update Affected Customers")}</h4>
                            </div>
                            <div className="modal-body">
                                <div className="prompt">
                                    <p>
                                        {this.localizeString("You have customers whose products are affected by your accepted and approved data.")}
                                    </p>
                                    <p>
                                        {this.localizeString("Do you want 3E Exchange to send updated reports to the affected customers?")}
                                    </p>
                                </div>
                                <div className="loading-spinner-container text-center">
                                    <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("No, don't update")}</button>
                                <button type="button" className="btn btn-toxnot-primary" onClick={async e => await this.promptUserToUpdateCustomersConfirmButton_click()}>{this.localizeString("Yes, send customer updates")}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="edit-custom-names-modal" className="modal fade full-screen-modal" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                <h4 className="modal-title">{this.localizeString("Enter Custom Names For Materials/Products")}</h4>
                            </div>
                            <div className="modal-body custom-names-modal-body">
                                <div className="btn-group">
                                    <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{this.localizeString("Apply the same Summary Row column value to all material names")} <span className="caret"></span></button>
                                    <ul className="dropdown-menu custom-names-dropdown-menu">
                                        {this.props.surveyMetadata.columnIdsInSummaryRows.map((value, index) => {
                                            return (
                                                <li key={index.toString()}>
                                                    <a role="button" onClick={e => this.props.setCustomNameForAll(value , this.props.supplierRowModels)}>
                                                        {(this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[value] ?? value)}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                                {" "}
                                <table className="table tn-table">
                                    <thead>
                                        <tr>
                                            <td>{this.localizeString("Name")}</td>
                                            <td>{this.localizeString("Supplier")}</td>
                                            <td>{this.localizeString("Custom Name")}</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.props.supplierRowModels
                                            .map((supplier, supplierIndex) => 
                                                supplier.requestedMaterials
                                                    .orderBy(material => material.name).map((material, materialIndex) => 
                                                        <tr key={supplier.rowIdentifier + "_" + materialIndex}>
                                                            <td>{material.name}</td>
                                                            <td>{supplier.supplierName}</td>
                                                            <td className="custom-name-input">
                                                                {material.inventoryId === null
                                                                    ?
                                                                    <input type="text" className="form-control" onChange={e => this.props.setCustomName(supplier.rowIdentifier, e.target.value, materialIndex)} value={this.props.supplierRowModels.find(s => s.rowIdentifier === supplier.rowIdentifier)?.requestedMaterials[materialIndex]?.customName ?? ""}></input>
                                                                    :
                                                                    <div className="input-group">
                                                                        <input type="text" className="form-control" onChange={e => this.props.setCustomName(supplier.rowIdentifier, e.target.value, materialIndex)} value={material.customName ?? ""}></input>
                                                                        <div className="input-group-btn">
                                                                            <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{this.localizeString("Row Values")} <span className="caret"></span></button>
                                                                            {material.inventoryId && <ul className="dropdown-menu dropdown-menu-right">
                                                                                {this.props.surveyMetadata.inventoryIdToSummaryRowContentsMap[material.inventoryId]?.rowSummaryContent?.map((summaryRow, summaryContentRowIndex) => (
                                                                                    <li key={supplierIndex.toString() + materialIndex.toString() + summaryContentRowIndex.toString()}>
                                                                                        <a role="button" onClick={e => this.props.setCustomName(supplier.rowIdentifier,summaryRow.value, materialIndex)}>
                                                                                            {(this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[summaryRow.columnId] ?? summaryRow.columnId) + ": " + (summaryRow.value.length > 36 ? summaryRow.value.substring(0, 25) + "..." : summaryRow.value)}
                                                                                        </a>
                                                                                    </li>))
                                                                                }
                                                                            </ul>}
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </td>
                                                        </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal">{this.localizeString("Done")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    // ===============================================================================

    private renderAddOfflineResponseModal = () => (
        <div id="add-offline-response-modal" className="modal fade" role="dialog">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                        <h4 className="modal-title">{this.localizeString("Add an Offline Response")}</h4>
                    </div>
                    <div className="modal-body">
                        {this.state.offlineResponseType === OfflineResponseType.Manual &&
                            <>
                                <div className="alert alert-info">
                                    <p>
                                        {this.localizeString("You can enter information manually if you receive data from a Supplier in an email or other source.")}
                                    </p>
                                    <p>
                                        {this.localizeString("Enter any response text and notes in the text boxes below, and select any files provided by the supplier to upload.")}
                                    </p>
                                    <p>
                                        {this.localizeString("3E Exchange will save this data with your Survey and mark the Survey as accepted.")}
                                    </p>
                                </div>
                                <div className="form-group">
                                    <label>{this.localizeString("Supplier Response Text")}</label>
                                    <textarea value={this.props.surveyUi.offlineResponseSupplierResponseInputText} onChange={e => this.props.setSurveyUIData({ offlineResponseSupplierResponseInputText: e.target.value })} rows={10} className="form-control" placeholder=""></textarea>
                                </div>
                                <div className="form-group">
                                    <label>{this.localizeString("File Attachments")}</label>
                                    <input type="file" className="form-control" onChange={e => this.props.setSurveyUIData({ offlineResponseAttachments: e.target.files })} placeholder="" multiple />
                                </div>
                                <div className="form-group">
                                    <label>{this.localizeString("Notes")}</label>
                                    <textarea rows={10} value={this.props.surveyUi.offlineResponseNotesInputText} onChange={e => this.props.setSurveyUIData({ offlineResponseNotesInputText: e.target.value })} className="form-control" placeholder=""></textarea>
                                </div>
                                <div className="alert alert-info">
                                    {this.localizeString("Processing...")}
                                </div>
                                <div className="alert alert-success">
                                    <p>
                                        {this.localizeString("Responses accepted!")}
                                    </p>
                                </div>
                                <div className="alert alert-danger">
                                    {this.localizeString("Sorry, there was a problem processing the responses. Please try again.")}
                                </div>
                            </>
                        }
                        {this.state.offlineResponseType === OfflineResponseType.File && this.state.offlineProccessId !== null &&
                            <div className="text-center">
                                <OfflineProcessProgress offlineProcessId={this.state.offlineProccessId} appServices={this.props.appServices} onComplete={async () => await this.dataCollectionImportResponse_complete()} centerProgressBar={true} />
                            </div>
                        }
                        {this.state.offlineResponseType === OfflineResponseType.None &&
                            <div className="offline-response-choices-container">
                                <div className="offline-response-choice-container" onClick={e => this.manualOfflineResponse_selected()}>
                                    {this.localizeString("Add the supplier's response manually.")}
                                </div>
                                <div className="offline-response-choice-container" onClick={e => this.uploadSupplierResponseFileOption_click()}>
                                    {this.localizeString("Upload the response from a XLSX file.")}
                                    <SelectFileForUpload buttonId={"upload-supplier-response-file-button"} fileSelected={this.offlineResponseFile_selected} acceptedUploadFileTypes={[".xlsx"]} localizedUploadButtonText={this.localizeString("Upload Response File")} hide={true} />
                                </div>
                            </div>
                        }
                    </div>
                    <div className="modal-footer">
                        <button type="button" id="accept-offline-response-cancel-button" className="btn btn-default" onClick={e => this.setState({ offlineResponseType: this.getInitalOfflineResponseType() })} data-dismiss="modal">{this.localizeString("Close")}</button>
                        {this.state.offlineResponseType === OfflineResponseType.Manual && <button type="button" id="accept-offline-response-confirm-button" className="btn btn-toxnot-primary" onClick={async e => await this.addOfflineResponseButton_click()}>{this.localizeString("Save Offline Response")}</button>}
                        <button type="button" id="accept-offline-response-done-button" className="btn btn-toxnot-primary initially-hidden" onClick={e => this.addManualOfflineResponseButton_click()}>{this.localizeString("Done")}</button>
                    </div>
                </div>
            </div>
        </div>
    );

    // ===============================================================================

    // Survey Templates

    // ===============================================================================

    private async applyTemplate(templateId: string) {
        const template = this.props.surveyMetadata.templates.firstOrDefault(t => t.templateId === templateId);

        if (template === null) {
            throw new Error(`template not found - id=${templateId}`);
        }

        for (let x = 0; x < template.scheduledReminders.length; x++) {
            const now = new Date();
            const dateToUse = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
            dateToUse.setDate(dateToUse.getDate() + template.scheduledReminders[x].scheduledDaysSinceRequestSent);
            template.scheduledReminders[x].scheduledSendDate = dateToUse.getTime();
            template.scheduledReminders[x].scheduledSendDateUnix = dateToUse.getTime();
            template.scheduledReminders[x].reminderId = null;
        }

        const bomQuestionnareIdToUse = template.bomQuestionnaireId != null ? (this.props.surveyMetadata.questionnaireTable[template.bomQuestionnaireId] ? template.bomQuestionnaireId : null) : null;
        const supplierQuestionnareIdToUse = template.supplierQuestionnaireId != null ? (this.props.surveyMetadata.questionnaireTable[template.supplierQuestionnaireId] ? template.supplierQuestionnaireId : null) : null;
        const attestationQuestionnareIdToUse = template.attestationQuestionnaireId != null ? (this.props.surveyMetadata.questionnaireTable[template.attestationQuestionnaireId] ? template.attestationQuestionnaireId : null) : null;

        // on the fly migration of legacy templates

        if (!template.bomColumns.some(f => f.columnId === "level")) {
            template.bomColumns.splice(0, 0, {
                columnId: "level",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["level"]
            });
        }

        if (!template.bomColumns.some(f => f.columnId === "part-name")) {
            template.bomColumns.splice(1, 0, {
                columnId: "part-name",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["part-name"]
            });
        }

        if (!template.bomColumns.some(f => f.columnId === "casrn")) {
            template.bomColumns.splice(2, 0, {
                columnId: "casrn",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["casrn"]
            });
        }

        if (!template.bomColumns.some(f => f.columnId === "percent-by-weight")) {
            template.bomColumns.splice(3, 0, {
                columnId: "percent-by-weight",
                ...SurveyStateController.columnIdToDefaultColumnRequiredFlagsTable["percent-by-weight"]
            });
        }

        const linkedMaterialsExists = SurveyStateController.doLinkedMaterialsExist(this.props);

        this.props.setSurveyRequestData({
            bomQuestionnaireId: bomQuestionnareIdToUse,
            supplierQuestionnaireId: supplierQuestionnareIdToUse,
            attestationQuestionnaireId: attestationQuestionnareIdToUse,
            languageList: template.languageList,
            nativeLanguage: template.nativeLanguage ?? "en",
            ccEmailList: template.ccEmailList.split(","),
            emailSubject: template.emailSubject,
            emailMessage: template.emailMessage,
            requestInstructions: template.requestInstructions,
            languageToEmailSubjectMap: template.languageToEmailSubjectMap,
            languageToEmailMessageMap: template.languageToEmailMessageMap,
            languageToRequestInstructionsMap: template.languageToRequestInstructionsMap,
            languageToCompletionInstructionsMap: template.languageToCompletionInstructionsMap,
            scheduledReminders: template.scheduledReminders,
            sdsRequested: template.sdsRequested,
            questionnaireAnswerThatMakesBomDataRequired: linkedMaterialsExists ? null : template.questionnaireAnswerThatMakesBomDataRequired,
            fields: template.bomColumns,
            completionInstructions: template.completionInstructions,
            useManufacturerLogo: template.useManufacturerLogo,
            preFillBomQuestionnaireFromSummaryData: template.preFillBomQuestionnaireFromSummaryData == null ? false : template.preFillBomQuestionnaireFromSummaryData,
            preFillSupplierQuestionnaireFromSummaryData: template.preFillSupplierQuestionnaireFromSummaryData == null ? false : template.preFillSupplierQuestionnaireFromSummaryData,
            makeBomDataRequiredOnQuestionnaireAnswer: template.makeBomDataRequiredOnQuestionnaireAnswer == null ? false : template.makeBomDataRequiredOnQuestionnaireAnswer,
            metadata: {
                requireSuppliersToDiscloseRegulatoryIngredients: template.requireSuppliersToDiscloseRegulatoryIngredients,
                additionalColumnIdIdentifiers: template.additionalColumnIdIdentifiers
            },
            includeAttestationLetter: template.includeAttestationLetter
        });

        if (template.dataRequested.complianceList.length > 0) { // equivalent to complianceDataAsked
            for (var i = 0; i < this.props.surveyRequest.dataRequested.complianceList.length; i++) {
                if (this.regulationChangedSideEffect !== undefined) {
                    // enable dependent BOM columns for the previous applied template requested regulations
                    this.regulationChangedSideEffect(this.props.surveyRequest.dataRequested.complianceList[i], false);
                }
            }
        }

        await this.props.setDataRequested({
            materialDisclosureAsked: linkedMaterialsExists ? false : template.dataRequested.materialDisclosureAsked,
            complianceList: template.dataRequested.complianceList,
            complianceDataAsked: template.dataRequested.complianceList.length > 0,
            circularityDataAsked: template.dataRequested.circularityDataAsked,
            documentsCertificationList: template.dataRequested.documentsCertificationList,
            documentsAsked: template.dataRequested.documentsCertificationList.length > 0,
            embodiedCarbonDataAsked: template.dataRequested.embodiedCarbonDataAsked,
            waterUseDataAsked: template.dataRequested.waterUseDataAsked,
            sustainabilityDataAsked: template.dataRequested.sustainabilityDataAsked,
            customQuestionsAsked: bomQuestionnareIdToUse != null || supplierQuestionnareIdToUse != null || attestationQuestionnareIdToUse != null,
            vocDataAsked: template.dataRequested.vocDataAsked,
            vocOtherDataAsked: template.dataRequested.vocOtherDataAsked,
            vocTransparencyDataAsked: template.dataRequested.vocTransparencyDataAsked,
            carbonOtherDataAsked: template.dataRequested.carbonOtherDataAsked,
            carbonTransparencyDataAsked: template.dataRequested.carbonTransparencyDataAsked
        });

        if (template.dataRequested.materialDisclosureAsked) {
            for (var i = 0; i < template.dataRequested.complianceList.length; i++) {
                if (this.regulationChangedSideEffect !== undefined) {
                    // disable dependent BOM columns for the current applied template requested regulations
                    this.regulationChangedSideEffect(template.dataRequested.complianceList[i], true);
                }
            }
        }

        this.props.setSurveyUIData({
            currentTemplateId: templateId
        });

        $("#survey-template-manage-modal").modal("hide");
        $("#survey-template-apply-modal").modal("hide");
    }

    // ==========================================================================

    private $getUpdateCustomersConfirmationModal() {
        return $("#prompt-to-update-customers-modal");
    }

    // ==========================================================================

    private async promptUserToUpdateCustomers(inventoryIds: string[], operationId: string) {
        const $modal = this.$getUpdateCustomersConfirmationModal();

        $modal.find(".modal-body .prompt").show();
        $modal.find(".modal-footer button").show();
        $modal.find(".modal-body .loading-spinner-container").hide();
        $modal.attr("data-inventory-ids", inventoryIds.toString());
        $modal.attr("data-operation-id", operationId);

        $modal.modal("show");
    }

    // ===============================================================================

    /**
     * Determines if a given template is "equal" to the current state of the survey request
     * returns true if it is, else false.
     * 
     * @param template the template to compare.
     */
    private isSurveyRequestEqualToTemplate(template: Models.SurveyTemplateDataModel) {

        //
        // Data Requested
        //

        if (template.dataRequested.materialDisclosureAsked !== this.props.surveyRequest.dataRequested.materialDisclosureAsked) {
            return false;
        }

        if (template.dataRequested.circularityDataAsked !== this.props.surveyRequest.dataRequested.circularityDataAsked) {
            return false;
        }

        if (template.dataRequested.embodiedCarbonDataAsked !== this.props.surveyRequest.dataRequested.embodiedCarbonDataAsked) {
            return false;
        }

        if (template.dataRequested.waterUseDataAsked !== this.props.surveyRequest.dataRequested.waterUseDataAsked) {
            return false;
        }

        if (!template.dataRequested.complianceList.equalTo(this.props.surveyRequest.dataRequested.complianceList)) {
            return false;
        }

        if (!template.dataRequested.documentsCertificationList.map(d => d.documentNameIfOther !== "" ? d.documentNameIfOther : d.documentCertificationType)
            .equalTo(this.props.surveyRequest.dataRequested.documentsCertificationList.map(d => d.documentNameIfOther !== "" ? d.documentNameIfOther : d.documentCertificationType))) {
            return false;
        }

        if (template.sdsRequested !== this.props.surveyRequest.sdsRequested) {
            return false;
        }

        //
        // Bom columns
        //

        if (!template.bomColumns.map(b => b.columnId).equalTo(this.props.surveyRequest.fields.map(f => f.columnId))) {
            return false;
        }

        if (!template.bomColumns.map(b => b.requiredInSummaryRow ? b.columnId + "1" : b.columnId + "0").equalTo(this.props.surveyRequest.fields.map(f => f.requiredInSummaryRow ? f.columnId + "1" : f.columnId + "0"))) {
            return false;
        }

        if (!template.bomColumns.map(b => b.requiredInLeafRows ? b.columnId + "1" : b.columnId + "0").equalTo(this.props.surveyRequest.fields.map(f => f.requiredInLeafRows ? f.columnId + "1" : f.columnId + "0"))) {
            return false;
        }

        if (!template.bomColumns.map(b => b.requiredInNonSummaryNonLeafRows ? b.columnId + "1" : b.columnId + "0").equalTo(this.props.surveyRequest.fields.map(f => f.requiredInNonSummaryNonLeafRows ? f.columnId + "1" : f.columnId + "0"))) {
            return false;
        }

        //
        // Questionnaires
        //

        if (template.supplierQuestionnaireId !== this.props.surveyRequest.supplierQuestionnaireId) {
            return false;
        }

        if (template.bomQuestionnaireId !== this.props.surveyRequest.bomQuestionnaireId) {
            return false;
        }


        if (template.attestationQuestionnaireId !== this.props.surveyRequest.attestationQuestionnaireId) {
            return false;
        }

        //
        // Instructions
        //

        if (template.requestInstructions !== this.props.surveyRequest.requestInstructions) {
            return false;
        }

        if (template.completionInstructions !== this.props.surveyRequest.completionInstructions) {
            return false;
        }

        //
        // Reminders
        //

        if (!template.scheduledReminders.map(r => r.scheduledDaysSinceRequestSent.toString()).equalTo(this.props.surveyRequest.scheduledReminders.map(r => r.scheduledDaysSinceRequestSent.toString()))) {
            return false;
        }

        //
        // Message settings
        //

        if (!template.ccEmailList.split(",").equalTo(this.props.surveyRequest.ccEmailList)) {
            return false;
        }

        if (template.emailMessage !== this.props.surveyRequest.emailMessage) {
            return false;
        }

        if (template.emailSubject !== this.props.surveyRequest.emailSubject) {
            return false;
        }

        //
        // Miscellaneous
        //

        if (template.useManufacturerLogo !== this.props.surveyRequest.useManufacturerLogo) {
            return false;
        }

        if (!template.languageList.equalTo(this.props.surveyRequest.languageList)) {
            return false;
        }

        return true;
    }


    // ===============================================================================

    /**
     * Determines if anything in the survey request has changed from a potential template
     * and returns true if the user should be prompted to save their changes. Else false is
     * returned.
     */
    private shouldAskAboutSavingChangesBeforeSendingOrDrafting() {
        // If there is no select survey template, always prompt the user to save before
        // sending.
        if (this.props.surveyUi.currentTemplateId === null) {
            return true;
        }

        // Else check to see if the user has changed anything from the selected template.
        const template = this.props.surveyMetadata.templates.find(t => t.templateId === this.props.surveyUi.currentTemplateId);

        if (!template) {
            throw new Error("Template Not Found");
        }

        // The user did not change anything for the template.
        if (this.isSurveyRequestEqualToTemplate(template)) {
            return false;
        }

        return true;
    }

    // ===============================================================================

    private sendSurveyWithPotentialSaveChangesOption = async () => {
        if (this.shouldAskAboutSavingChangesBeforeSendingOrDrafting()) {
            this.props.showSaveTemplateModal(Models.SaveTemplateMode.SendSurveysOnContinue);
        }
        else {
            await this.props.sendSurvey();
        }
    }

    // ===============================================================================

    private suppliersHaveNamesAndEmails() {
        return !(this.props.supplierRowModels.some(s => (s.supplierName.trim() === "") || (s.contactEmail.trim() === "")));
    }

    // ===============================================================================

    private hasAtLeastOneSupplier() {
        return (this.props.supplierRowModels.length > 0);
    }

    // ===============================================================================

    // UI Events

    // ===============================================================================

    private sendSurveyAgainUpdateConfirmationBtn_click = () => {
        $('#survey-update-confirmation-modal').modal('hide');
        this.sendSurveyWithPotentialSaveChangesOption();
    }

    // ===============================================================================

    private saveSurveyAsDraftButton_click = () => {
        if (this.props.supplierRowModels.map(s => s.supplierName.trim()).getDuplicates().length > 0) {
            $('#duplicate-supplier-name-warning-modal').modal('show');
            return;
        }

        if (this.shouldAskAboutSavingChangesBeforeSendingOrDrafting()) {
            this.props.showSaveTemplateModal(Models.SaveTemplateMode.SaveDraftsOnContinue);
        }
        else {
            this.props.saveNewSurveyAsDraft();
        }
    }

    // ===============================================================================

    private saveTemplateButton_click = async () => {
        //
        // In "default" mode, the user is just saving a template while still editing the survey.
        // So require that they pick something to save.
        //
        // If in "sending" or "drafting" mode, the user doesn't have to choose to save the template.
        //

        // Did the user pick an option to save to?
        const anyRadioSelected = this.props.surveyUi.saveTemplateAnyRadioSelected;

        const targetTemplateId = this.props.surveyUi.saveTemplateSelectedTemplateId;

        if ((this.props.surveyUi.saveTemplateMode === Models.SaveTemplateMode.Default) && !anyRadioSelected) {
            alert(this.localizeString("Please select an option to save."));
        }

        if (anyRadioSelected && (targetTemplateId == null) && (this.props.surveyUi.newTemplateName.trim().length === 0)) {
            alert(this.localizeString("Please set a template name."));
            return;
        }

        if (anyRadioSelected) {
            await this.props.saveTemplate(this.props.surveyUi.saveTemplateSelectedTemplateId);
        }

        if (this.props.surveyUi.saveTemplateMode === Models.SaveTemplateMode.SendSurveysOnContinue) {
            this.props.sendSurvey();
        }
        else if (this.props.surveyUi.saveTemplateMode === Models.SaveTemplateMode.SaveDraftsOnContinue) {
            this.props.saveNewSurveyAsDraft();
        }
    }

    // ===============================================================================

    private removeValidationConfirmationButton_click = () => {
        this.props.setSurveyAsNoValidationRequired();

        $("#remove-validation-warning-modal").modal("hide");
    }

    // ===============================================================================

    private sendSurveyButton_click = async () => {
        if (!this.hasAtLeastOneSupplier()) {
            alert(this.localizeString("At least one supplier must be provided."));
            this.props.setSurveyUIData({ currentPage: 0 });
            return;
        }
        if (!this.suppliersHaveNamesAndEmails()) {
            alert(this.localizeString("The company name and email address fields cannot be blank."));
            this.props.setSurveyUIData({ currentPage: 0 });
            return;
        }
        if (this.props.supplierRowModels.map(s => s.supplierName.trim()).getDuplicates().length > 0) {
            $('#duplicate-supplier-name-warning-modal').modal('show');
            return;
        }
        if (this.props.surveyRequest.fromName === "") {
            $("#user-add-name-prompt-modal").modal("show");
        }
        else if (this.props.surveyRequest.languageList.length > 1) {
            $("#select-language-to-send-in-modal").modal("show");
        }
        else {
            this.sendSurveyWithPotentialSaveChangesOption();
        }
    }

    // ===============================================================================

    private selectLanguageToSendInSubmitButton_click = async () => {
        $("#select-language-to-send-in-modal").modal("hide");
        this.sendSurveyWithPotentialSaveChangesOption();
    }

    // ===============================================================================

    private sendSurveyAgainButton_click = () => {
        if (this.props.surveyRequest.state !== 3) {
            $("#survey-update-confirmation-modal").modal("show");
        } else {
            this.sendSurveyWithPotentialSaveChangesOption();
        }
    }

    // ===============================================================================

    private saveTemplateAsButton_click = () => {
        this.props.showSaveTemplateModal(Models.SaveTemplateMode.Default);
    }

    // ===============================================================================

    private addLanguageButton_click = () => {
        const $modal = $("#add-language-modal");

        $modal.find(".alert-warning").hide();

        $modal.modal("show");
    }

    // ===============================================================================

    private addLanguageSubmitButton_click = () => {
        const $modal = $("#add-language-modal");

        const selectedLanguage = $modal.find("#new-language-selector").val()!.toString();

        const languageAlreadyExists = this.props.surveyRequest.languageList.some(l => l === selectedLanguage);

        if (languageAlreadyExists) {
            $modal.find(".alert-warning").show();
            return;
        }

        if (this.props.surveyRequest.nativeLanguage === null) {
            this.props.surveyRequest.nativeLanguage = this.props.appServices.getUserCultureNameOrDefault();
        }

        const newLanguageList = this.props.surveyRequest.languageList.slice();

        if (newLanguageList.indexOf(this.props.surveyRequest.nativeLanguage) === -1) {
            newLanguageList.push(this.props.surveyRequest.nativeLanguage);
        }

        newLanguageList.push(selectedLanguage);

        this.props.surveyRequest.languageToEmailSubjectMap[selectedLanguage] = "";
        this.props.surveyRequest.languageToEmailMessageMap[selectedLanguage] = "";
        this.props.surveyRequest.languageToRequestInstructionsMap[selectedLanguage] = "";
        this.props.surveyRequest.languageToCompletionInstructionsMap[selectedLanguage] = "";

        for (let reminder of this.props.surveyRequest.scheduledReminders) {
            reminder.languageToReminderMessageMap[selectedLanguage] = "";
        }

        this.props.setSurveyRequestData({
            languageList: newLanguageList,
            nativeLanguage: this.props.surveyRequest.nativeLanguage,
            languageToEmailSubjectMap: this.props.surveyRequest.languageToEmailSubjectMap,
            languageToEmailMessageMap: this.props.surveyRequest.languageToEmailMessageMap,
            languageToRequestInstructionsMap: this.props.surveyRequest.languageToRequestInstructionsMap,
            languageToCompletionInstructionsMap: this.props.surveyRequest.languageToCompletionInstructionsMap,
            scheduledReminders: this.props.surveyRequest.scheduledReminders
        });

        this.props.setSurveyUIData({ currentLanguage: selectedLanguage });

        $modal.modal("hide");
    }

    // ===============================================================================

    private removeLanguageButton_click = () => {
        const $modal = $("#remove-language-modal");

        $("#remove-language-confirmation-container").hide();
        $("#remove-language-selection-container").show();
        $("#remove-language-confirm-button").hide();

        $modal.modal("show");
    }

    // ===============================================================================

    private removeLanguageSubmitButton_click = (languageToRemove: string) => {
        const $modal = $("#remove-language-modal");

        const newLanguageList = this.props.surveyRequest.languageList.filter(l => l !== languageToRemove);

        delete this.props.surveyRequest.languageToEmailSubjectMap[languageToRemove];
        delete this.props.surveyRequest.languageToEmailMessageMap[languageToRemove];
        delete this.props.surveyRequest.languageToRequestInstructionsMap[languageToRemove];
        delete this.props.surveyRequest.languageToCompletionInstructionsMap[languageToRemove];

        if (newLanguageList.length === 0) {
            this.props.surveyRequest.nativeLanguage = null;
        }

        if (this.props.surveyRequest.languageToSendIn === languageToRemove) {
            this.props.surveyRequest.languageToSendIn = null;
        }

        for (let reminder of this.props.surveyRequest.scheduledReminders) {
            delete reminder.languageToReminderMessageMap[languageToRemove];
        }

        this.props.setSurveyRequestData({
            languageList: newLanguageList,
            nativeLanguage: this.props.surveyRequest.nativeLanguage,
            languageToSendIn: this.props.surveyRequest.languageToSendIn,
            scheduledReminders: this.props.surveyRequest.scheduledReminders,
            languageToEmailSubjectMap: this.props.surveyRequest.languageToEmailSubjectMap,
            languageToEmailMessageMap: this.props.surveyRequest.languageToEmailMessageMap,
            languageToRequestInstructionsMap: this.props.surveyRequest.languageToRequestInstructionsMap,
            languageToCompletionInstructionsMap: this.props.surveyRequest.languageToCompletionInstructionsMap
        });

        if (this.props.surveyUi.currentLanguage === languageToRemove) {
            this.props.setSurveyUIData({
                currentLanguage: newLanguageList[0] ?? this.props.surveyRequest.nativeLanguage
            });
        }

        $modal.modal("hide");
    }

    // ===============================================================================

    private sendReminderButton_click = () => {
        this.props.resetSendReminderState();
        $("#send-reminder-modal").modal("show");
    }

    // ===============================================================================

    private removeValidationButton_click = () => {
        $("#remove-validation-warning-modal").modal("show");
    }

    // ===============================================================================

    private exportSurveyButton_click = () => {
        window.location.href = `/Api/ExportSurvey?requestId=${encodeURIComponent(this.props.surveyRequest.requestId!)}`
    }

    // ===============================================================================

    private acceptSupplierResponseConfirmButton_click = async () => {
        const $modal = $("#accept-supplier-response-modal");

        const historyId = $modal.data("history-id") as string;

        $modal.find(".alert-danger").hide();
        $modal.find(".alert-info").show();

        $modal.find("#accept-supplier-response-confirm-button").prop("disabled", true);

        const requestId = this.props.surveyRequest.requestId;

        if (requestId === null) {
            throw new Error("request id not set");
        }

        this.props.appServices.trackEvent("accepted-survey-response");

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.surveyAcceptSupplierLevelResponse(requestId, historyId);

        if (!apiResult.success) {
            $("#accept-supplier-response-modal .alert").hide();
            $("#accept-supplier-response-modal .alert-danger").show();
            $("#accept-supplier-response-confirm-button").prop("disabled", false);

            return;
        }

        if (apiResult.result === ResultType.Ok) {
            const newHistoryList = [...this.props.surveyRequest.responseHistoryList];

            const indexToUpdate = newHistoryList.findIndex(h => h.historyId === historyId);

            const historyEntryToUpdate = newHistoryList[indexToUpdate];

            newHistoryList[indexToUpdate] = {
                ...historyEntryToUpdate,
                dateAccepted: new Date().toISOString(),
                supplierQuestionnaireData: {
                    ...historyEntryToUpdate.supplierQuestionnaireData!,
                    dateAccepted: new Date().toISOString()
                }
            };

            this.props.setSurveyRequestData({
                state: apiResult.data!.newRequestState,
                responseHistoryList: newHistoryList
            });

            $("#accept-supplier-response-modal .alert").hide();
            $("#accept-supplier-response-modal .alert-success").show();
            $("#accept-supplier-response-cancel-button").hide();
            $("#accept-supplier-response-confirm-button").prop("disabled", false);
            $("#accept-supplier-response-confirm-button").hide();
            $("#accept-supplier-response-done-button").show();
        }
        else if (apiResult.result === ResultType.QuotaExceeded) {
            $modal.modal("hide");
            // TODO
            //this.showFileQuotaModal();
        }
        else {
            $("#accept-supplier-response-modal .alert").hide();
            $("#accept-supplier-response-modal .alert-danger").show();
            $("#accept-supplier-response-confirm-button").prop("disabled", false);
        }
    }

    // ===============================================================================
    
    private async assignStatusToUnAvailbleMaterials() {
        const $modal = $("#assign-status-modal");

        const $obsoleteCheckbox = $("#mark-workflow-status-obsolete");
        const $statusCheckboxes = $(".status-checkbox");

        // Get the value of "Mark Workflow Status as Obsolete"
        const markAsObsolete = $obsoleteCheckbox.prop("checked");

        // Get all checked unavailable material checkboxes
        const selectedMaterials: string[] = $(".unavailable-material-name-checkbox:checked").map(function () {
            return $(this).data("target-inventory-id");
        }).get();
    
        // Get the selected status (only one checkbox can be checked at a time)
        const selectedStatus = $("input[name='productStatusCheckbox']:checked").val();
        
        if (selectedMaterials.length === 0) {
            alert("Please select at least one unavailable material.");
            return;
        }

        try {
            const serviceClient = new ToxnotServiceClient();
            // Show processing alert
            $(".processing-alert").show();
            $(".success-alert, .error-alert").hide();
    
            await serviceClient.assignStatusToInventories(
                selectedMaterials,
                selectedStatus ? String(selectedStatus) : "",
                this.props.appServices.userContext?.teamOwnerId,
                markAsObsolete
            );
    
            $(".processing-alert").hide();
            $(".success-alert").show();
        } catch (error) {
            console.error("An error occurred while assigning statuses:", error);
            $(".processing-alert").hide();
            $(".error-alert").show();
        }

        $statusCheckboxes.prop("checked", false);
        $obsoleteCheckbox.prop("checked", false);

        setTimeout(() => {
            $(".success-alert, .error-alert").hide();
            $modal.modal("hide");
        }, 3000);
    }
  
    // ===============================================================================

    private async acceptBomResponsesConfirmButton_click() {
        const $modal = $("#accept-bom-responses-modal");

        const isOpen = this.props.surveyRequest.bomType === 1;

        const acceptBomsContext = $modal.data("accept-boms-context") as AcceptBomsContext;

        const overwriteOriginals = !isOpen && $("#accept-bom-responses-radio-overwrite-original").is(":checked");

        $("#accept-bom-responses-modal .alert-danger").hide();
        $("#accept-bom-responses-modal .alert-info").show();

        $("#accept-bom-responses-confirm-button").prop("disabled", true);

        const requestId = this.props.surveyRequest.requestId;

        if (requestId === null) {
            throw new Error("request id must be set");
        }

        this.props.appServices.trackEvent("accepted-survey-response");

        const serviceClient = new ToxnotServiceClient();

        const acceptBomResponsesApiResult = await serviceClient.surveyAcceptBomResponses(requestId, acceptBomsContext, !overwriteOriginals);

        if (!acceptBomResponsesApiResult.success || !acceptBomResponsesApiResult.data) {
            $("#accept-bom-responses-modal .alert").hide();
            $("#accept-bom-responses-modal .alert-danger").show();
            $("#accept-bom-responses-confirm-button").prop("disabled", false);

            return;
        }

        $("#accept-bom-responses-cancel-button").prop("disabled", true);

        const $acceptStatusModal = $("#offline-accept-progress-modal");
        const offlineProcessDataResponses = this.props.surveyMetadata.offlineProcessDataResponses;
        const offlineProcessQueuedMaterials = this.props.surveyMetadata.offlineProcessQueuedMaterials;

        offlineProcessDataResponses.push(acceptBomResponsesApiResult.data);

        for (var i = 0; i < acceptBomResponsesApiResult.data?.context?.requestorResponseInventoryIds?.length; i++) {
            offlineProcessQueuedMaterials.push(acceptBomResponsesApiResult.data?.context.requestorResponseInventoryIds[i]);
        }

        this.props.setSurveyMetadata({ offlineProcessDataResponses: offlineProcessDataResponses });
        this.props.setSurveyMetadata({ offlineProcessQueuedMaterials: offlineProcessQueuedMaterials });

        $acceptStatusModal.off("hidden.bs.modal").on("hidden.bs.modal", async () => {
            if (this.props.match.params.surveyId && this.props.match.path === CreateAndEditSurveyComponentPaths.ExistingSurveyPath) {
                try {
                    await this.props.getExistingSurveyState(this.props.match.params.surveyId);
                } catch {
                    this.props.setSurveyUIData({ showNotFoundErrorScreen: true });
                }

                this.props.setSurveyUIData({ dataLoaded: true });
            }

            this.props.setSurveyUIData({ activeAcceptOfflineProcessId: "" });
        });

        this.props.setSurveyUIData({ activeAcceptOfflineProcessId: acceptBomResponsesApiResult.data?.operationId.toString() ?? "" });

        $modal.off("hidden.bs.modal").on("hidden.bs.modal", () => {
            $("#accept-bom-responses-cancel-button").prop("disabled", false);
            $("#accept-bom-responses-confirm-button").prop("disabled", false);

            $acceptStatusModal.modal("show");
        });

        const $checkboxes = $(".material-name-checkbox[data-history-entry-id]");
        const $selectAllCheckbox = $(".material-select-all-checkbox");

        $checkboxes.prop("checked", false);
        $selectAllCheckbox.prop("checked", false);

        $modal.modal("hide");
    }

    // ==========================================================================

    private promptUserToUpdateCustomersConfirmButton_click = async () => {
        const $modal = this.$getUpdateCustomersConfirmationModal();

        $modal.find(".modal-body .prompt").hide();
        $modal.find(".modal-footer button").hide();
        $modal.find(".modal-body .loading-spinner-container").show();

        const serviceClient = new ToxnotServiceClient();

        const inventoryIds = $modal.attr("data-inventory-ids")?.split(",") ?? [];

        const apiResult = await serviceClient.updateAffectedCustomersAboutInventories(inventoryIds);

        if (!apiResult.success) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        const operationId = $modal.attr("data-operation-id") ?? "";

        const deleteResult = await serviceClient.offlineProcessDelete(operationId);

        if (deleteResult.success) {
            const offlineProcessDataResponses = this.props.surveyMetadata.offlineProcessDataResponses;
            const filteredOfflineProcessDataResponses = offlineProcessDataResponses.filter(i => i.operationId !== operationId);

            this.props.setSurveyMetadata({ offlineProcessDataResponses: filteredOfflineProcessDataResponses });
        }

        $modal.modal("hide");
    }

    // ==========================================================================

    private addOfflineResponseButton_click = async() => {
        $("#add-offline-response-modal .alert-info").show();
        await this.props.addOfflineResponse();
        $("#add-offline-response-modal .alert-info").hide();
        if (this.props.surveyUi.addOfflineResponseSuccess) {
            $("#add-offline-response-modal .alert-success").show();
        } else {
            $("#add-offline-response-modal .alert-danger").show();
        }
        $("#accept-offline-response-cancel-button").hide();
        $("#accept-offline-response-confirm-button").hide();
        $("#accept-offline-response-done-button").show();
    }

    // =========================================================================

    private showOfflineResponseButton_click = () => {
        if (this.getUserContextForSignedInUser().userCapabilities.exportToOfflineSurveyEnabled) {
            this.setState({ offlineResponseType: OfflineResponseType.None })
        }
        else if (!this.getUserContextForSignedInUser().averyDennisonFeaturesEnabled) {
            this.manualOfflineResponse_selected();
        }

        $("#add-offline-response-modal").modal("show");
    }

    // ===============================================================================

    private generalRevisionModalSubmitButton_click = async () => {
        const $modal = $("#ask-for-general-revision-modal");

        const revisionInstructionsText = $modal.find("#revision-message-textarea").val()!.toString().trim();

        if (revisionInstructionsText.length === 0) {
            alert(this.localizeString("Please enter a message."));
            return;
        }

        $modal.find("#revision-message-textarea").closest(".form-group").hide();
        $modal.find(".modal-body .alert").hide();
        $modal.find(".modal-body .alert-info").show();
        $modal.find("#submit-revision-cancel-button").hide();
        $modal.find("#submit-revision-submit-button").hide();
        $modal.find("#submit-revision-done-button").hide();

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.surveySendGeneralRevisionRequest(
            this.props.surveyRequest!.requestId!,
            revisionInstructionsText);

        $modal.find(".modal-body .alert").hide();
        if (apiResult.result == "InvalidEmailToken") {
            $modal.find(".alert-warning").show();
            $modal.find("#submit-revision-cancel-button").show();
        } else if (!apiResult.success) {
            $modal.find(".alert-danger").show();
            $modal.find("#submit-revision-cancel-button").show();
        }
        else {
            $modal.find(".alert-success").show();
            $modal.find("#submit-revision-done-button").show();
        }

        this.props.getExistingSurveyState(this.props.surveyRequest.requestId!);
    }

    
    // ===============================================================================

    private sendRevisionsRequestsDoneButton_click = () => {
        $("#sending-revision-requests-modal").modal("hide");

        this.props.setSurveyUIData({
            sendGranularRevisionRequestMode: false
        });
    }

    // ===============================================================================

    private sendGeneralRevisionRequestButton_click = () => {
        $("#empty-granular-revision-requests-modal").modal("hide");
        this.props.setSurveyUIData({ sendGranularRevisionRequestMode: true });
    }
 
    // ===============================================================================
 
    private cmrtSendSurveyRevisionRequest_click = () => {
        $("#empty-granular-revision-requests-modal").modal("hide");
        $("#cmrt-revision-request-confirmation-dialog").modal("show");
    }
 
    // ===============================================================================
 
    // Granular Revision Requests

    // ===============================================================================

    private renderViewGraularRevisionRequestView(historyEntryId: string) {
        if (this.props.surveyRequest === null) {
            throw new Error("survey request not set");
        }

        const historyEntry = this.props.surveyRequest.responseHistoryList.firstOrDefault(r => r.historyId === historyEntryId);

        if (!historyEntry) {
            throw new Error("history entry not found");
        }

        const tempBomRevisionRequestTable: { [id: string]: Models.BomRevisionRequest } = {};

        for (let inventoryData of historyEntry.inventoryDataList) {
            if (inventoryData.revisionRequest !== null) {
                tempBomRevisionRequestTable[inventoryData.revisionRequest.responseInventoryId] = inventoryData.revisionRequest;
            }
        }

        const revisionRequest: Models.SurveyHistoryEntryGranularRevisionRequest = {
            generalRevisionInstructions: historyEntry.revisionEmailText,
            supplierQuestionnaireRevisionRequest: historyEntry.supplierQuestionnaireData?.revisionRequest ?? null,
            bomRevisionRequestTable: tempBomRevisionRequestTable,
            dateSentIso8601: historyEntry.dateRevisionSent,
            dateRevisionResponseReceivedIso8601: historyEntry.dateRevisionResponseReceived,
            sentTo: this.props.surveyRequest.sendToEmail
        };

        return this.renderGanularRevisionRequestDetail(revisionRequest, false);
    }

    // ===============================================================================

    private renderSendGranularRevisionRequestView() {
        if (this.props.surveyUi.revisionRequestManager === null) {
            throw new Error("revision manager not set");
        }

        const revisionRequest = this.props.surveyUi.revisionRequestManager.getGranularRevisionRequest();

        if (revisionRequest === null) {
            throw new Error("revision request not set");
        }

        return this.renderGanularRevisionRequestDetail(revisionRequest, true);
    }

    // ===============================================================================

    private renderGanularRevisionRequestDetail(revisionRequest: Models.SurveyHistoryEntryGranularRevisionRequest, sendAllowed: boolean) {
        if (this.props.surveyRequest === null) {
            throw new Error("survey request not set");
        }
        
        // Determine which questionnaire to use based on compliance requirements
        const isConflictMineralsV2 = this.props.surveyRequest.dataRequested.complianceList?.includes(RegulationManager.ConflictMineralsV2RegulationId);

        const conflictMineralsV2Questionnarie = this.state.surveyResponse?.supplierQuestionnaireData?.questionnaire; // Keep this for potential specific V2 logic if needed elsewhere

        const supplierLevelQuestionnaire = this.props.surveyRequest.supplierQuestionnaireId !== null
            ? this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.supplierQuestionnaireId]
            : null;

        const relevantQuestionnaire = isConflictMineralsV2
            ? conflictMineralsV2Questionnarie // Use V2 questionnaire if requested (could be undefined)
            : supplierLevelQuestionnaire; // Otherwise, use the standard supplier questionnaire (could be null)

        return (
            <div id="send-granular-revision-request-container" className="tn-section">
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        <p>
                            <button type="button" className="btn btn-default" onClick={e => this.props.setSurveyUIData({ sendGranularRevisionRequestMode: false, viewGranularRevisionRequestHistoryEntryId: null })}><span className="glyphicon glyphicon-chevron-left"></span> {this.localizeString("Back")}</button>
                            {sendAllowed &&
                                <React.Fragment>
                                    {" "}
                                    <button type="button" id="send-revision-request-submit-button" className="btn btn-toxnot-primary" onClick={async e => await this.sendRevisionRequestSubmitButton_click()}>{this.localizeString("Send Revision Request")}</button>
                                </React.Fragment>
                            }
                        </p>
                    </div>
                </div>
                <div id="revision-container" className="tn-section-row">
                    <div className="tn-section-col-12">
                        <div className="grouped-data">
                            {!sendAllowed &&
                                <div id="view-previous-revision-container" className="group">
                                    <div className="title">
                                        {this.localizeString("Status")}
                                    </div>
                                    <div className="content">
                                        {(revisionRequest.dateSentIso8601 !== null) &&
                                            <React.Fragment>
                                                <p>
                                                    {this.localizeString("Revision Request sent to:")} {revisionRequest.sentTo} {new Date(revisionRequest.dateSentIso8601).toLocaleDateString()}
                                                </p>
                                                <p>
                                                    {(revisionRequest.dateRevisionResponseReceivedIso8601 !== null)
                                                        ? (
                                                            <React.Fragment>
                                                                {this.localizeString("Revision Reponse Recieved:")} {new Date(revisionRequest.dateRevisionResponseReceivedIso8601).toLocaleDateString()}
                                                            </React.Fragment>
                                                        )
                                                        : (
                                                            <i>{this.localizeString("No Response Received")}</i>
                                                        )
                                                    }
                                                </p>
                                            </React.Fragment>
                                        }
                                    </div>
                                </div>
                            }
                            <div className="group">
                                <div className="title">
                                    {this.localizeString("Email Message")}
                                </div>
                                <div className="content">
                                    <div id="granular-revision-request-general-instructions-email">
                                        {sendAllowed
                                            ? <textarea className="form-control" rows={10} onChange={e => this.props.surveyUi.revisionRequestManager?.setGeneralEmailInstructions(e.target.value)} defaultValue={revisionRequest.generalRevisionInstructions}></textarea>
                                            : (
                                                <React.Fragment>
                                                    {revisionRequest.generalRevisionInstructions.split('\n').map((s, i) =>
                                                        <React.Fragment key={i}>{s.trim()}<br /></React.Fragment>)
                                                    }
                                                </React.Fragment>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            {(revisionRequest.supplierQuestionnaireRevisionRequest !== null) &&
                                <div className="group supplier-survey-revision-request-container">
                                    <div className="title">
                                        {this.localizeString("Supplier-level Questionnaire Revisions")}
                                    </div>
                                    <div className="content">
                                        {(revisionRequest.supplierQuestionnaireRevisionRequest.generalRevisionInstructions.length > 0) &&
                                            <p className="supplier-questionnaire-general-instructions">
                                                {revisionRequest.supplierQuestionnaireRevisionRequest.generalRevisionInstructions
                                                    .split('\n')
                                                    .map((s, i) => <React.Fragment key={i}>{s.trim()}<br /></React.Fragment>)
                                                }
                                            </p>
                                        }
                                        {/* Check relevantQuestionnaire is not null or undefined before passing to renderQuestionRevisions */}
                                        {((revisionRequest.supplierQuestionnaireRevisionRequest.supplierQuestionRevisions.length > 0) && (relevantQuestionnaire != null)) &&
                                            this.renderQuestionRevisions(revisionRequest.supplierQuestionnaireRevisionRequest.supplierQuestionRevisions, relevantQuestionnaire)
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                        <div className="grouped-data bom-revision-requests-container">
                            {Object.keys(revisionRequest.bomRevisionRequestTable).map((responseInventoryId, index) => this.renderBomRevisionRequest(revisionRequest.bomRevisionRequestTable[responseInventoryId], index))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderBomRevisionRequest(bomRevisionRequest: Models.BomRevisionRequest, bomRevisionRequestIndex: number) {

        // handle empty request

        if (bomRevisionRequest.generalRevisionInstructions.length === 0 &&
            RevisionRequestManager.allSpecificRevisionInstructionsAreEmpty(bomRevisionRequest)) {
            return (<div key={bomRevisionRequestIndex}></div>);
        }

        const responseInventoryMetadata = this.props.surveyMetadata.responseInventoryMetadataTable[bomRevisionRequest.responseInventoryId];

        if (!responseInventoryMetadata) {
            this.props.surveyUi.revisionRequestManager?.removeRevisionFromRequestTable(bomRevisionRequest.responseInventoryId);
            return;
        }

        const dynRegFilter = (dynReg: DynamicRegulation) => (bomRevisionRequest.dynamicRegulationRevisionInstructions[dynReg.regulationId] ?? "").length > 0;
        const dynRegsWithInstructions = this.props.surveyMetadata.dynamicRegulations.filter(dynRegFilter);
        dynRegsWithInstructions.sort((a, b) => a.config.name.localeCompare(b.config.name));

        const dynRegPageNamesWithInstructions = dynRegsWithInstructions.map(dynReg => (
            {
                pageName: dynReg.config.name,
                instructions: bomRevisionRequest.dynamicRegulationRevisionInstructions[dynReg.regulationId]
            }));

        return (
            <div key={bomRevisionRequestIndex} className="group bom-revision-request">
                <div className="title">
                    {responseInventoryMetadata.inventoryName.emptyCoalesce(this.localizeString("Unnamed"))}
                </div>
                <div className="content">
                    {bomRevisionRequest.generalRevisionInstructions.length > 0 &&
                        <div className="group">
                            <div className="title">{this.localizeString("General Instructions")}</div>
                            <div className="content">
                                {bomRevisionRequest.generalRevisionInstructions
                                    .split('\n')
                                    .map((s, i) => <React.Fragment key={i}>{s.trim()}<br /></React.Fragment>)
                                }
                            </div>
                        </div>
                    }
                    {bomRevisionRequest.bomColumnRevisions.length > 0 &&
                        <div className="group">
                            <div className="title">{this.localizeString("BOM Data")}</div>
                            <div className="content">
                                <div className="revision-container">
                                    <table className="table tn-table">
                                        <thead>
                                            <tr>
                                                <td>{this.localizeString("Column")}</td>
                                                <td>{this.localizeString("Instructions")}</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bomRevisionRequest.bomColumnRevisions.map((columnRevision, index) => {

                                                return (
                                                    <tr key={index}>
                                                        <td>{this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[columnRevision.columnId]}</td>
                                                        <td>
                                                            {columnRevision.revisionInstructions
                                                                .split('\n')
                                                                .map((s, i2) => <React.Fragment key={i2}>{s.trim()}<br /></React.Fragment>)
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    }
                    {dynRegPageNamesWithInstructions.map(pair => {
                            return this.renderBomContentRevisionInstructions(pair.pageName, pair.instructions);
                        })
                    }
                    {bomRevisionRequest.sustainabilityRevisionInstructions.length > 0 &&
                        this.renderBomContentRevisionInstructions(this.localizeString("Sustainability Data"), bomRevisionRequest.sustainabilityRevisionInstructions)
                    }
                    {bomRevisionRequest.circularityRevisionInstructions.length > 0 &&
                        this.renderBomContentRevisionInstructions(this.localizeString("Circularity Data"), bomRevisionRequest.circularityRevisionInstructions)
                    }
                    {bomRevisionRequest.bomQuestionnaireQuestionRevisions.length > 0 &&
                        <div className="group">
                            <div className="title">{this.localizeString("Questionnaire")}</div>
                            <div className="content">
                                {this.renderQuestionRevisions(bomRevisionRequest.bomQuestionnaireQuestionRevisions, this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId!])}
                            </div>
                        </div>
                    }
                    {bomRevisionRequest.attestationQuestionnaireQuestionRevisions.length > 0 &&
                        <div className="group">
                            <div className="title">{this.localizeString("Attestation Questionnaire")}</div>
                            <div className="content">
                                {this.renderQuestionRevisions(bomRevisionRequest.attestationQuestionnaireQuestionRevisions, this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.attestationQuestionnaireId!])}
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderQuestionRevisions(questionRevisions: Models.QuestionRevision[], questionnaire: Models.Questionnaire) {
        return (
            <div className="questions-container">
                <div className="revision-container">
                    <table className="table tn-table">
                        <thead>
                            <tr>
                                <td>{this.localizeString("Question")}</td>
                                <td>{this.localizeString("Instructions")}</td>
                            </tr>
                        </thead>
                        <tbody>
                            {questionRevisions.map((questionRevision, index) => {
                                const page = questionnaire.pages[questionRevision.pageIndex];
                                const question = page?.questions[questionRevision.questionIndex];

                                return (
                                    <tr key={index}>
                                        <td>{question?.text ?? ""}</td>
                                        <td>
                                            {questionRevision.revisionInstructions
                                                .split('\n')
                                                .map((s, i2) => <React.Fragment key={i2}>{s.trim()}<br /></React.Fragment>)
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderBomContentRevisionInstructions(localizedTitle: string, instructions: string) {
        return (
            <div className="group">
                <div className="title">{localizedTitle}</div>
                <div className="content">
                    {instructions
                        .split('\n')
                        .map((s, i) => <React.Fragment key={i}>{s.trim()}<br /></React.Fragment>)
                    }
                </div>
            </div>
        );
    }

    // ===============================================================================

    private sendRevisionRequestSubmitButton_click = async () => {
        const hasCMRTRegulation = this.props.surveyRequest.dataRequested.complianceList?.includes(RegulationManager.ConflictMineralsV2RegulationId);
        
        const revisionRequest = this.props.surveyUi.revisionRequestManager?.getGranularRevisionRequest();
        if (!revisionRequest) {
            throw new Error("no revision request found");
        }
        
        const hasSupplierQuestionnaireRevisions = revisionRequest.supplierQuestionnaireRevisionRequest?.supplierQuestionRevisions
            ? Object.values(revisionRequest.supplierQuestionnaireRevisionRequest.supplierQuestionRevisions).some(item => item.pageIndex == 0)
            : false;
        
        const hasInventoryRevisions = Object.values(revisionRequest.bomRevisionRequestTable).some(item => item !== null);
        
        if (hasCMRTRegulation &&  (hasSupplierQuestionnaireRevisions || hasInventoryRevisions)) {
            this.cmrtSendSurveyRevisionRequest_click();
            return;
        }
        else
        {
            await this.sendGranularRevisonRequestConfirmClick();
        }
    }

    private sendGranularRevisonRequestConfirmClick = async () => {

        const revisionRequest = this.props.surveyUi.revisionRequestManager?.getGranularRevisionRequest();
        if (!revisionRequest) {
            throw new Error("no revision request found");
        }
        const $modal = $("#sending-revision-requests-modal");

        $modal.find(".alert").hide();
        $modal.find(".modal-footer button").hide();

        $modal.find(".alert-info").show();

        $modal.modal("show");

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.surveySendGranularRevisionRequest(
            this.props.surveyRequest!.requestId!,
            this.props.surveyRequest!.responseHistoryList[0].historyId,
            revisionRequest
        );

        $modal.find(".alert").hide();
        if (apiResult.result == "InvalidEmailToken") {
            $modal.find(".alert-warning").show();
        } else if (!apiResult.success) {
            $modal.find(".alert-danger").show();
        }
        else {
            $modal.find(".alert-success").show();
        }

        $modal.find(".modal-footer button").show();

        this.props.getExistingSurveyState(this.props.match.params.surveyId!);
    }
 
    // ===============================================================================
 
    private sendSpecificRevisionsConfirmClick = async () => {
        // Call the existing logic to send revisions
        $("#cmrt-revision-request-confirmation-dialog").modal("hide");
        await this.sendGranularRevisonRequestConfirmClick();
    }
 
    // ===============================================================================
 
    private manualOfflineResponse_selected = () => {
        this.setState({
            offlineResponseType: OfflineResponseType.Manual
        },
        () => {
            $("#add-offline-response-modal .alert").hide();
            $("#accept-offline-response-cancel-button").show();
            $("#accept-offline-response-confirm-button").show();
            $("#accept-offline-response-done-button").hide();
        });
    }

    // ===============================================================================

    private offlineResponseFile_selected = async (file: File) => {
        this.setState({
            offlineResponseType: OfflineResponseType.File,
            offlineResponseFileUploadState: OfflineResponseFileUploadStateType.UploadingFile
        });

        const serviceClient = new ToxnotServiceClient();

        const fileResult = await serviceClient.uploadGeneralFile(file.name, "", 0, false, false, file);

        if (fileResult.data === null || !fileResult.success) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        this.setState({
            offlineResponseFileUploadState: OfflineResponseFileUploadStateType.UploadedFile
        });

        const requestId = this.props.surveyRequest.requestId;

        if (requestId === null) {
            throw new Error("Request Id cannot be null when uploading an offline response");
        }

        const offlineProcessResult = await serviceClient.dataCollectionImportFileResponse(requestId, fileResult.data.fileId);

        if (offlineProcessResult.data === null || !offlineProcessResult.success) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        this.setState({
            offlineProccessId: offlineProcessResult.data
        });
    }

    // ===============================================================================

    private dataCollectionImportResponse_complete = async () => {
        const requestId = this.props.match.params.surveyId;

        if (!requestId) {
            throw new Error("Request id should not be null here");
        }

        $('#add-offline-response-modal').modal('hide');

        this.setState({
            offlineResponseType: this.getInitalOfflineResponseType(),
            offlineProccessId: null
        });

        this.props.setSurveyUIData({ dataLoaded: false });
        await this.props.getExistingSurveyState(requestId);
        this.props.setSurveyUIData({ dataLoaded: true });
    }

    // ===============================================================================

    private uploadSupplierResponseFileOption_click = () => {
        // Current response being imported already.
        if (this.state.offlineProccessId !== null) {
            this.setState({ offlineResponseType: OfflineResponseType.File });
            return;
        }

        document.getElementById("upload-supplier-response-file-button")?.click();
    }

    private addManualOfflineResponseButton_click = () => {
        $('#add-offline-response-modal').modal('hide');

        this.setState({ offlineResponseType: this.getInitalOfflineResponseType() });
    }
}

// ===============================================================================

const mapDispatchToProps = { ...SurveyStateController.actionCreators, updateSupplierRowModels: SelectSuppliersMaterialsStateController.actionCreators.updateSupplierRowModelsFromSurveyEditor, 
    setCustomName: SelectSuppliersMaterialsStateController.actionCreators.setCustomName, setCustomNameForAll: SelectSuppliersMaterialsStateController.actionCreators.setCustomNameForAllMaterials,  };
const mapStateToProps = (state: ApplicationState) => ({ ...state.createAndEditSurveysState, appServices: state.appServices, ...state.selectSuppliersMaterialsTableState})

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateAndEditSurveysMain) as any);
